import { Fragment, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
    Typography, IconButton, Box, Grid2, Card, CardContent, TextField, Button, Divider, ToggleButtonGroup, Chip,
} from "@mui/material"
import {
    ToggleButton, MenuItem, Select, InputLabel, FormControl, FormControlLabel, Checkbox, Table, TableBody, TableHead, TableRow, Autocomplete,
    Radio, RadioGroup,
} from "@mui/material"
import {
    TableCell, Paper, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Switch, LinearProgress, Alert,
} from "@mui/material";
import {
    Add, Remove, Delete, TableBar, LocalShipping, TakeoutDining, Search, Save, Print, PauseCircle, Visibility,
} from "@mui/icons-material"
import {
    Restore, Payments, QrCode2, LocalOffer,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Navbar from '../setup/Navbar'
import '../../services/GlobalVariables'
import { getItemsForPOS } from '../../features/StockManagement/posSlice'
import { getTables, getEmployees, assignTable, releaseTable, transferTable, saveOrder, updateOrder, holdOrder, resumeOrder, clearHoldOrder, resumeOrderByTable, getHeldOrders } from '../../features/StockManagement/posBusinessSlice'
import { fetchDispatchCounters } from '../../features/setup/dispatchCounterSlice'
import { fetchTakeawayCounters } from '../../features/setup/takeawayCounterSlice'
import { fetchActiveDeals } from '../../features/setup/dealsSlice'
import { getLastOpenTill } from '../../features/closing/closingSlice'
import Loading from '../../BuiltInComponent/Loading'
import axios from 'axios'
import { generateOrderIdempotencyKey } from '../../utils/idempotencyKeyGenerator'
import { KOT_PRINTER_MODES, fetchPrintBehaviorSettings } from '../../utils/printBehaviorSettings'
import { DEFAULT_POS_ORDER_TYPE_SETTINGS, fetchPosOrderTypeSettings } from '../../utils/posOrderTypeSettings'
/**
 * Restaurant POS Form — React + MUI (No react-hook-form)
 * - Order types: Dine‑in (Table), Takeaway, Delivery
 * - Foodpanda option: toggle + fields (orderId, rider, commission %, status)
 * - Menu with categories, search, quick-add
 * - Cart with quantity, modifiers, notes, line discount
 * - Charges: service, tax/VAT, delivery fee, coupon
 * - Payments: multi‑method split (Cash, Card, Wallet), due change calc
 * - Actions: Save (Park), Print KOT/Bill, Hold/Resume, Clear
 */

// ---- Mock Data ----
// const MENU = [
//     { id: "b1", name: "Chicken Biryani", price: 350, category: "Rice" },
//     { id: "b2", name: "Beef Biryani", price: 420, category: "Rice" },
//     { id: "k1", name: "Chicken Karahi (Half)", price: 1200, category: "Karahi" },
//     { id: "k2", name: "Chicken Karahi (Full)", price: 2200, category: "Karahi" },
//     { id: "n1", name: "Naan", price: 40, category: "Bread" },
//     { id: "r1", name: "Raita", price: 80, category: "Sides" },
//     { id: "d1", name: "Drink 250ml", price: 120, category: "Drinks" },
//     { id: "d2", name: "Drink 1.5L", price: 300, category: "Drinks" },
//     { id: "p1", name: "Paratha", price: 90, category: "Bread" },
//     { id: "t1", name: "Tea", price: 80, category: "Drinks" },
// ];


const escapeReceiptHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getReceiptPreviewWidth = (paperMm) => {
    const width = Number(paperMm || 80);
    return `${Math.max(220, Math.round(width * 4.2))}px`;
};

const TABLES = Array.from({ length: 16 }).map((_, i) => ({
    id: i + 1,
    label: `T${i + 1}`,
}));

const CASH_COLLECTION_OPTIONS = [5000, 4000, 3000, 2000, 1000, 500];

const POS_NOTICE_META = {
    success: {
        title: "Success",
        color: "success",
    },
    warning: {
        title: "Action Required",
        color: "warning",
    },
    error: {
        title: "Action Required",
        color: "error",
    },
    info: {
        title: "Action Required",
        color: "info",
    },
};

const inferNoticeSeverity = (message) => {
    const text = String(message || "").toLowerCase();

    if (text.includes("error") || text.includes("failed") || text.includes("unable")) {
        return "error";
    }

    if (
        text.includes("cannot") ||
        text.includes("warning") ||
        text.includes("missing") ||
        text.includes("incomplete") ||
        text.includes("please ")
    ) {
        return "warning";
    }

    if (
        text.includes("success") ||
        text.includes("printed") ||
        text.includes("completed") ||
        text.includes("saved") ||
        text.includes("resumed")
    ) {
        return "success";
    }

    return "info";
};

const normalizePosNotice = (input) => {
    if (typeof input === "object" && input !== null && !Array.isArray(input)) {
        const message = String(input.message || "");
        const severity = POS_NOTICE_META[input.severity] ? input.severity : inferNoticeSeverity(message);
        return {
            message,
            severity,
            title: input.title || POS_NOTICE_META[severity].title,
        };
    }

    const message = String(input || "");
    const severity = inferNoticeSeverity(message);
    return {
        message,
        severity,
        title: POS_NOTICE_META[severity].title,
    };
};

let posNoticeHandler = null;

const setPosNoticeHandler = (handler) => {
    posNoticeHandler = typeof handler === "function" ? handler : null;
};

function alert(input) {
    const notice = normalizePosNotice(input);

    if (posNoticeHandler) {
        posNoticeHandler(notice);
        return;
    }

    if (typeof window !== "undefined" && typeof window.alert === "function") {
        window.alert(notice.message);
    }
}

// ---- Utilities ----
const currency = (n) => (n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "PKR",
    maximumFractionDigits: 0,
});

const THERMAL_PREVIEW_WIDTH = 42;

const thermalClamp = (value, max = THERMAL_PREVIEW_WIDTH) => {
    const text = String(value ?? '');
    if (text.length <= max) return text;
    if (max <= 3) return text.slice(0, max);
    return `${text.slice(0, max - 3)}...`;
};

const thermalCenter = (value, width = THERMAL_PREVIEW_WIDTH) => {
    const text = thermalClamp(value, width);
    const totalPadding = Math.max(0, width - text.length);
    const left = Math.floor(totalPadding / 2);
    const right = totalPadding - left;
    return `${' '.repeat(left)}${text}${' '.repeat(right)}`;
};

const thermalLeftRight = (left, right, width = THERMAL_PREVIEW_WIDTH) => {
    const rightText = thermalClamp(String(right ?? ''), width);
    const maxLeftLength = Math.max(0, width - rightText.length - 1);
    const leftText = thermalClamp(String(left ?? ''), maxLeftLength || width);
    const spacing = width - leftText.length - rightText.length;

    if (spacing < 1) {
        return `${thermalClamp(leftText, width - rightText.length - 1)} ${rightText}`;
    }

    return `${leftText}${' '.repeat(spacing)}${rightText}`;
};

const thermalWrap = (value, width = THERMAL_PREVIEW_WIDTH) => {
    const text = String(value ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return [];

    const words = text.split(' ');
    const lines = [];
    let current = '';

    words.forEach((word) => {
        if (!current) {
            current = word;
            return;
        }

        if ((current.length + 1 + word.length) <= width) {
            current += ` ${word}`;
        } else {
            lines.push(current);
            current = word;
        }
    });

    if (current) {
        lines.push(current);
    }

    return lines;
};

const thermalMoney = (value) => `Rs.${Number(value || 0).toLocaleString()}`;

const getScheduledDiscountTotal = (item, quantity = item?.qty) => (
    Math.max(0, Number(item?.scheduledUnitDiscount || 0)) * Math.max(0, Number(quantity || 0))
);

const withCalculatedLineDiscount = (item, quantity = item?.qty, manualDiscount = item?.manualLineDiscount) => {
    const qty = Math.max(0, Number(quantity || 0));
    const scheduledDiscount = getScheduledDiscountTotal(item, qty);
    const normalizedManualDiscount = Math.max(0, Number(manualDiscount || 0));
    return {
        ...item,
        qty,
        manualLineDiscount: normalizedManualDiscount,
        lineDiscount: scheduledDiscount + normalizedManualDiscount,
    };
};

function calcTotals(state) {
    const grossSubTotal = state.items.reduce((sum, it) => sum + Number(it.price || 0) * Number(it.qty || 0), 0);
    const scheduledDiscount = state.items.reduce((sum, it) => sum + getScheduledDiscountTotal(it), 0);
    const manualLineDiscount = state.items.reduce((sum, it) => sum + Number(it.manualLineDiscount || 0), 0);
    const lineDiscount = state.items.reduce((sum, it) => sum + Number(it.lineDiscount || 0), 0);
    const subTotal = Math.max(0, grossSubTotal - lineDiscount);
    const service = state.applyService ? Math.round((state.serviceRate / 100) * subTotal) : 0;
    const taxBase = subTotal + service;
    const tax = state.applyTax ? Math.round((state.taxRate / 100) * taxBase) : 0;
    const delivery = state.orderType === "delivery" ? Number(state.deliveryFee) || 0 : 0;
    const coupon = state.couponAmount || 0;
    const overallDiscount = state.overallDiscount || 0;
    const grand = Math.max(0, taxBase + tax + delivery - coupon - overallDiscount);
    const paid = state.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const change = Math.max(0, paid - grand);
    const due = Math.max(0, grand - paid);
    return {
        grossSubTotal, scheduledDiscount, manualLineDiscount, lineDiscount, subTotal,
        service, tax, delivery, coupon, overallDiscount, grand, paid, change, due
    };
}

const normalizeVariantToken = (value) => String(value || "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim();

const formatVariantLabel = (value) => String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const VARIANT_ORDER = ["small", "medium", "large", "extra large", "x large", "xl", "quarter", "half", "full", "leg", "chest"];

const getVariantSortRank = (value) => {
    const normalized = normalizeVariantToken(value);
    const index = VARIANT_ORDER.indexOf(normalized);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const toPositiveInt = (value, fallback = 1) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : fallback;
};

const createRegularLineKey = (itemCode) => `item:${itemCode}`;
const createDealLineKey = (dealID, itemCode) => `deal:${dealID}:${itemCode}`;

const getLineKey = (item) => {
    const explicit = String(item?.lineKey || "").trim();
    if (explicit) return explicit;
    const itemCode = item?.itemCode || item?.id;
    if (item?.isDealItem && item?.dealID) {
        return createDealLineKey(item.dealID, itemCode);
    }
    return createRegularLineKey(itemCode);
};

const getDealItemBaseQty = (item) => {
    if (!item?.isDealItem) return null;
    return toPositiveInt(item.dealItemBaseQty, toPositiveInt(item.qty, 1));
};

const getDealBundleQty = (item) => {
    if (!item?.isDealItem) return null;
    const explicit = Number(item?.dealBundleQty || 0);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const baseQty = getDealItemBaseQty(item);
    return Math.max(1, Math.round(Number(item?.qty || 0) / Math.max(baseQty || 1, 1)));
};

const syncDealItemsToBundleQty = (items, dealID, nextBundleQty) =>
    items
        .map((item) => {
            if (!(item?.isDealItem && item?.dealID === dealID)) {
                return item;
            }

            const baseQty = getDealItemBaseQty(item);
            const qty = baseQty * nextBundleQty;

            return {
                ...item,
                qty,
                dealItemBaseQty: baseQty,
                dealBundleQty: nextBundleQty,
            };
        })
        .filter((item) => Number(item?.qty || 0) > 0);

const toPositiveDecimal = (value, fallback = 1) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const toTrimmedOrFallback = (value, fallback = "Unit") => {
    const cleanValue = String(value || "").trim();
    return cleanValue || fallback;
};

const getBaseUnitName = (item) => toTrimmedOrFallback(item?.baseUnitName || item?.inUnit, "Unit");
const getReportingUnitName = (item) => toTrimmedOrFallback(item?.reportingUnitName || item?.outUnit || getBaseUnitName(item), getBaseUnitName(item));
const getBaseUnitsPerReportingUnit = (item) => toPositiveDecimal(item?.baseUnitsPerReportingUnit || item?.outQuantity, 1);

const attachReportingMeta = (item, overrides = {}) => ({
    ...item,
    baseUnitName: overrides.baseUnitName || getBaseUnitName({ ...item, ...overrides }),
    reportingUnitName: overrides.reportingUnitName || getReportingUnitName({ ...item, ...overrides }),
    baseUnitsPerReportingUnit: overrides.baseUnitsPerReportingUnit || getBaseUnitsPerReportingUnit({ ...item, ...overrides }),
});

const formatQtyWithUnit = (qty, unitName) => {
    const normalizedQty = Number(qty || 0);
    const safeQty = Number.isFinite(normalizedQty) ? normalizedQty : 0;
    const trimmedUnit = String(unitName || "").trim();
    return trimmedUnit ? `${safeQty} ${trimmedUnit}` : String(safeQty);
};

const getDealDisplayUnitPrice = (item) => {
    const unitPrice = Number(item?.price || item?.unitPrice || 0);
    const baseQty = getDealItemBaseQty(item) || Number(item?.dealItemBaseQty || 0) || 1;
    return unitPrice > 0 ? unitPrice * baseQty : 0;
};

const buildGroupedCartItems = (items) => {
    const rows = [];
    const dealRows = new Map();

    items.forEach((item) => {
        if (!item?.isDealItem || !item?.dealID) {
            rows.push({
                kind: "item",
                key: getLineKey(item),
                lineKey: getLineKey(item),
                rawItem: item,
                dealID: null,
                name: item.name,
                qty: item.qty,
                qtyLabel: formatQtyWithUnit(item.qty, item.reportingUnitName || item.baseUnitName),
                price: Number(item.price || 0),
                total: Number(item.price || 0) * Number(item.qty || 0) - Number(item.lineDiscount || 0),
                lineDiscount: Number(item.lineDiscount || 0),
                note: item.note || "",
                isDealItem: false,
                components: [],
            });
            return;
        }

        const component = {
            key: getLineKey(item),
            name: item.name,
            qtyLabel: formatQtyWithUnit(item.qty, item.baseUnitName),
        };
        const existingGroup = dealRows.get(item.dealID);

        if (!existingGroup) {
            const bundleQty = getDealBundleQty(item);
            const group = {
                kind: "deal",
                key: `deal-group:${item.dealID}`,
                lineKey: getLineKey(item),
                rawItem: item,
                dealID: item.dealID,
                name: item.dealName || item.name,
                qty: bundleQty,
                qtyLabel: String(bundleQty),
                price: getDealDisplayUnitPrice(item),
                total: Number(item.price || 0) * Number(item.qty || 0) - Number(item.lineDiscount || 0),
                lineDiscount: 0,
                note: item.note || "",
                isDealItem: true,
                components: [component],
            };
            dealRows.set(item.dealID, group);
            rows.push(group);
            return;
        }

        existingGroup.components.push(component);
        existingGroup.total += Number(item.price || 0) * Number(item.qty || 0) - Number(item.lineDiscount || 0);

        if (Number(item.price || 0) > 0) {
            existingGroup.price = getDealDisplayUnitPrice(item);
            existingGroup.qty = getDealBundleQty(item);
            existingGroup.qtyLabel = String(getDealBundleQty(item));
            existingGroup.lineKey = getLineKey(item);
            existingGroup.rawItem = item;
            existingGroup.note = item.note || existingGroup.note;
        }
    });

    return rows;
};

const buildOrderItemPayload = (item) => {
    const normalizedItem = attachReportingMeta(item);
    return {
        id: normalizedItem.itemCode || normalizedItem.id,
        lineKey: getLineKey(normalizedItem),
        name: normalizedItem.name,
        qty: normalizedItem.qty,
        price: normalizedItem.price,
        lineDiscount: normalizedItem.lineDiscount || 0,
        note: normalizedItem.note || null,
        isDealItem: normalizedItem.isDealItem || false,
        dealID: normalizedItem.dealID || null,
        dealName: normalizedItem.dealName || null,
        dealItemBaseQty: normalizedItem.dealItemBaseQty || null,
        dealBundleQty: normalizedItem.dealBundleQty || null,
        baseUnitName: normalizedItem.baseUnitName,
        reportingUnitName: normalizedItem.reportingUnitName,
        baseUnitsPerReportingUnit: normalizedItem.baseUnitsPerReportingUnit
    };
};

// ---- Reducer ----
const initialState = {
    orderType: "dinein", // dinein | takeaway | delivery
    branchID: 1, // Default branch (will be set from logged-in user)
    tableNo: "",
    guests: 1,
    waiterID: "",
    waiter: "",
    isEmployeeOrder: false,
    employeeID: "",
    employeeName: "",
    riderID: "",
    rider: "",
    dispatchCounterId: "",
    takeawayCounterId: "",
    items: [],
    notes: "",
    currentOrderID: null, // Track if this is a resumed order
    currentHoldID: null, // Track the hold ID if resumed
    kotPrinted: false, // Track if KOT has been printed (for takeaway locking)
    applyService: false,
    serviceRate: 5,
    applyTax: false,
    taxRate: 8,
    deliveryFee: 0,
    deliveryZoneId: null, // Selected delivery zone
    deliveryZone: "", // Zone name for display
    deliveryPaymentMethod: "cod", // cod | prepaid for delivery orders
    couponCode: "",
    couponAmount: 0,
    overallDiscount: 0,
    payments: [{ method: "Cash", amount: "" }],
    // Foodpanda
    foodpanda: {
        enabled: false,
        orderId: "",
        rider: "",
        commissionPct: 18,
        status: "Preparing", // Preparing | Picked | Delivered | Cancelled
        platformFee: 0,
    },
    // Delivery details
    customer: {
        name: "",
        phone: "",
        address: "",
    },
    holdTickets: [], // for parked orders
    busy: false,
    pendingReduction: null, // Track pending quantity reduction for warning
    pendingDeletion: null, // Track pending item deletion for warning
    changedItems: [], // Track item changes for cancellation KOT
};

function reducer(state, action) {
    switch (action.type) {
        case "SET":
            console.log("SET action.payload:", action.payload);
            return { ...state, ...action.payload };
        case "ASSIGN_TABLE": {
            // Just store the table label in state - backend handles the reservation
            return {
                ...state,
                tableNo: action.label || state.tableNo,
            };
        }
        case "RELEASE_TABLE": {
            // Just clear the table label from state - backend handles releasing
            return {
                ...state,
                tableNo: "",
            };
        }
        case "ADD_ITEM": {
            // Block if takeaway and KOT is already printed
            if (state.orderType === 'takeaway' && state.kotPrinted) {
                alert('Cannot add items after KOT has been printed for Takeaway orders!');
                return state;
            }

            // Check if this is a deal
            if (action.item.isDeal) {
                // Add all deal items to cart (without adding deal parent)
                let newItems = [...state.items];
                const dealItems = action.item.dealItems || [];
                const dealPrice = parseFloat(action.item.price);

                // Check if this exact deal already exists in cart
                const existingDealItems = newItems.filter(i => i.dealID === action.item.dealID && i.isDealItem);

                if (existingDealItems.length > 0) {
                    const nextBundleQty = getDealBundleQty(existingDealItems[0]) + 1;
                    newItems = syncDealItemsToBundleQty(newItems, action.item.dealID, nextBundleQty);
                } else {
                    // Add new deal items
                    dealItems.forEach((dealItem, index) => {
                        // First item gets full deal price divided by qty to avoid multiplication
                        const baseQty = toPositiveInt(dealItem.quantity, 1);
                        const itemCode = dealItem.itemCode;
                        const itemPrice = index === 0 ? dealPrice / baseQty : 0;

                        newItems.push({
                            ...attachReportingMeta(dealItem),
                            id: itemCode,
                            itemCode: itemCode,
                            lineKey: createDealLineKey(action.item.dealID, itemCode),
                            name: dealItem.itemName,
                            price: itemPrice,
                            category: "Deal Item",
                            qty: baseQty,
                            note: "",
                            lineDiscount: 0,
                            modifiers: [],
                            isDealItem: true,
                            dealID: action.item.dealID,
                            dealName: action.item.name,
                            dealDescription: action.item.dealDescription,
                            dealItemBaseQty: baseQty,
                            dealBundleQty: 1
                        });
                    });
                }

                return { ...state, items: newItems };
            }

            // Regular item handling
            const regularLineKey = getLineKey({ ...action.item, isDealItem: false });
            const exist = state.items.find((i) => getLineKey(i) === regularLineKey && !i.isDealItem);
            if (exist) {
                return {
                    ...state,
                    items: state.items.map((i) => (
                        getLineKey(i) === regularLineKey && !i.isDealItem
                            ? withCalculatedLineDiscount(i, i.qty + 1)
                            : i
                    )),
                };
            }
            const scheduledUnitDiscount = Math.max(0, Number(action.item.scheduledUnitDiscount || 0));
            return {
                ...state,
                items: [
                    ...state.items,
                    {
                        ...attachReportingMeta(action.item),
                        itemCode: action.item.itemCode || action.item.id,
                        lineKey: regularLineKey,
                        qty: 1,
                        note: "",
                        scheduledUnitDiscount,
                        manualLineDiscount: 0,
                        lineDiscount: scheduledUnitDiscount,
                        modifiers: [], // e.g., spicy level, extra cheese
                    },
                ],
            };
        }
        case "INC": {
            // Block if takeaway and KOT is already printed
            if (state.orderType === 'takeaway' && state.kotPrinted) {
                alert('Cannot increase quantity after KOT has been printed for Takeaway orders!');
                return state;
            }

            // Check if item is KOT printed (optional info only - always allow increase for dinein)
            const item = state.items.find(i => getLineKey(i) === action.lineKey);
            if (!item) {
                return state;
            }
            if (item && item.kotPrinted && item.kotPrintedQuantity) {
                // Just log, don't block - increases are always allowed for dinein
                console.log(`Increasing KOT-printed item: ${item.name} (${item.kotPrintedQuantity} → ${item.qty + 1})`);
            }

            // If this is a deal item, increase all items in the deal
            if (item?.isDealItem) {
                const nextBundleQty = getDealBundleQty(item) + 1;
                return {
                    ...state,
                    items: syncDealItemsToBundleQty(state.items, item.dealID, nextBundleQty),
                };
            }

            return {
                ...state,
                items: state.items.map((i) => (
                    getLineKey(i) === action.lineKey
                        ? withCalculatedLineDiscount(i, i.qty + 1)
                        : i
                )),
            };
        }
        case "DEC": {
            // Block if takeaway and KOT is already printed
            if (state.orderType === 'takeaway' && state.kotPrinted) {
                alert('Cannot decrease quantity after KOT has been printed for Takeaway orders!');
                return state;
            }

            const item = state.items.find(i => getLineKey(i) === action.lineKey);
            if (!item) {
                return state;
            }
            const newQty = item?.isDealItem
                ? getDealItemBaseQty(item) * Math.max(1, getDealBundleQty(item) - 1)
                : Math.max(1, item.qty - 1);

            // Check if reducing below printed quantity (only for dinein)
            if (state.orderType === 'dinein' && item && item.kotPrinted && item.kotPrintedQuantity && newQty < item.kotPrintedQuantity) {
                const wasteQty = item.kotPrintedQuantity - newQty;

                // Store pending reduction for confirmation (will be handled by UI)
                if (action.skipWarning) {
                    // User confirmed, proceed
                    if (item.isDealItem) {
                        // Decrease all items in this deal
                        return {
                            ...state,
                            items: state.items
                                .map((i) => {
                                    if (i.isDealItem && i.dealID === item.dealID) {
                                        const nextBundleQty = Math.max(0, getDealBundleQty(i) - 1);
                                        return {
                                            ...i,
                                            qty: getDealItemBaseQty(i) * nextBundleQty,
                                            dealBundleQty: nextBundleQty
                                        };
                                    }
                                    return i;
                                })
                                .filter((i) => i.qty > 0),
                        };
                    }
                    return {
                        ...state,
                        items: state.items
                            .map((i) => (getLineKey(i) === action.lineKey ? withCalculatedLineDiscount(i, newQty) : i))
                            .filter((i) => i.qty > 0),
                    };
                } else {
                    // Block and let UI handle warning
                    return {
                        ...state,
                        pendingReduction: {
                            itemId: item.itemCode || item.id,
                            lineKey: getLineKey(item),
                            isDealItem: item.isDealItem || false,
                            dealID: item.dealID || null,
                            itemName: item.name,
                            oldQty: item.qty,
                            newQty: newQty,
                            kotPrintedQuantity: item.kotPrintedQuantity,
                            wasteQty: wasteQty
                        }
                    };
                }
            }

            // Normal decrease (not KOT printed or not below printed qty)
            if (item?.isDealItem) {
                // Decrease all items in this deal
                const nextBundleQty = Math.max(1, getDealBundleQty(item) - 1);
                return {
                    ...state,
                    items: syncDealItemsToBundleQty(state.items, item.dealID, nextBundleQty),
                };
            }

            return {
                ...state,
                items: state.items
                    .map((i) => (getLineKey(i) === action.lineKey ? withCalculatedLineDiscount(i, newQty) : i))
                    .filter((i) => i.qty > 0),
            };
        }
        case "REMOVE": {
            // Block if takeaway and KOT is already printed
            if (state.orderType === 'takeaway' && state.kotPrinted) {
                alert('Cannot remove items after KOT has been printed for Takeaway orders!');
                return state;
            }

            const item = state.items.find(i => getLineKey(i) === action.lineKey);
            if (!item) {
                return state;
            }

            // If this is a deal item, remove all items in the deal
            if (item?.isDealItem) {
                return {
                    ...state,
                    items: state.items.filter((i) => !(i.isDealItem && i.dealID === item.dealID))
                };
            }

            return { ...state, items: state.items.filter((i) => getLineKey(i) !== action.lineKey) };
        }
        case "UPDATE_ITEM":
            // If an overallDiscount is applied, prevent setting per-line discounts
            if (state.overallDiscount && action.patch && Object.prototype.hasOwnProperty.call(action.patch, 'manualLineDiscount')) {
                const { manualLineDiscount, ...rest } = action.patch;
                return {
                    ...state,
                    items: state.items.map((i) => (
                        getLineKey(i) === action.lineKey
                            ? withCalculatedLineDiscount({ ...i, ...rest }, i.qty, 0)
                            : i
                    )),
                };
            }
            return {
                ...state,
                items: state.items.map((i) => {
                    if (getLineKey(i) !== action.lineKey) return i;
                    if (action.patch && Object.prototype.hasOwnProperty.call(action.patch, 'manualLineDiscount')) {
                        return withCalculatedLineDiscount({ ...i, ...action.patch }, i.qty, action.patch.manualLineDiscount);
                    }
                    return { ...i, ...action.patch };
                }),
            };
        case "UPDATE_DEAL_ITEMS":
            return {
                ...state,
                items: state.items.map((item) => (
                    item?.isDealItem && item?.dealID === action.dealID
                        ? { ...item, ...action.patch }
                        : item
                )),
            };
        case "ADD_PAYMENT":
            return { ...state, payments: [...state.payments, { method: "Cash", amount: "" }] };
        case "UPDATE_PAYMENT":
            return {
                ...state,
                payments: state.payments.map((p, idx) => (idx === action.index ? { ...p, ...action.patch } : p)),
            };
        case "REMOVE_PAYMENT":
            return { ...state, payments: state.payments.filter((_, idx) => idx !== action.index) };
        case "APPLY_COUPON":
            return { ...state, couponCode: action.code, couponAmount: action.amount };
        case "APPLY_OVERALL_DISCOUNT": {
            // When applying overall discount, clear any per-line discounts
            const items = state.items.map((i) => (
                i.isDealItem ? i : withCalculatedLineDiscount(i, i.qty, 0)
            ));
            return { ...state, overallDiscount: action.amount || 0, items };
        }
        case "HOLD": {
            // Create a held ticket snapshot - backend handles table reservations
            const ticket = {
                id: Date.now(),
                when: new Date().toLocaleString(),
                snapshot: state,
            };

            return {
                ...initialState,
                holdTickets: [
                    ...state.holdTickets,
                    ticket,
                ],
            };
        }
        case "RESUME":
            return { ...action.ticket.snapshot, holdTickets: state.holdTickets.filter((t) => t.id !== action.ticket.id) };
        case "CONFIRM_REDUCTION": {
            // User confirmed the reduction - apply it and log for cancellation KOT
            const pending = state.pendingReduction;
            if (!pending) return state;

            const changedItems = state.changedItems || [];
            changedItems.push({
                itemId: pending.itemId,
                lineKey: pending.lineKey,
                itemName: pending.itemName,
                oldQuantity: pending.oldQty,
                newQuantity: pending.newQty,
                changeType: 'DECREASE',
                changeAmount: pending.oldQty - pending.newQty,
                kotPrintedQuantity: pending.kotPrintedQuantity
            });

            return {
                ...state,
                items: pending.isDealItem
                    ? (() => {
                        const targetItem = state.items.find((i) => getLineKey(i) === pending.lineKey);
                        const nextBundleQty = Math.max(1, (getDealBundleQty(targetItem) || 1) - 1);
                        return syncDealItemsToBundleQty(state.items, pending.dealID, nextBundleQty);
                    })()
                    : state.items.map((i) =>
                        getLineKey(i) === pending.lineKey ? { ...i, qty: pending.newQty } : i
                    ).filter((i) => i.qty > 0),
                pendingReduction: null,
                changedItems: changedItems
            };
        }
        case "CANCEL_REDUCTION":
            // User cancelled the reduction warning
            return { ...state, pendingReduction: null };
        case "CONFIRM_DELETION": {
            // User confirmed the deletion - remove item and log for cancellation KOT
            const pending = state.pendingDeletion;
            if (!pending) return state;

            const changedItems = state.changedItems || [];
            changedItems.push({
                itemId: pending.itemId,
                lineKey: pending.lineKey,
                itemName: pending.itemName,
                oldQuantity: pending.quantity,
                newQuantity: 0,
                changeType: 'CANCEL',
                changeAmount: pending.quantity,
                kotPrintedQuantity: pending.kotPrintedQuantity,
                wasteQuantity: pending.wasteQty
            });

            return {
                ...state,
                items: pending.isDealItem
                    ? state.items.filter((i) => !(i.isDealItem && i.dealID === pending.dealID))
                    : state.items.filter((i) => getLineKey(i) !== pending.lineKey),
                pendingDeletion: null,
                changedItems: changedItems
            };
        }
        case "CANCEL_DELETION":
            // User cancelled the deletion warning
            return { ...state, pendingDeletion: null };
        case "CLEAR":
            return {
                ...initialState,
                branchID: state.branchID,
                holdTickets: state.holdTickets,
                orderType: state.orderType,
                kotPrinted: false
            };
        case "MARK_ALL_ITEMS_KOT_PRINTED":
            return {
                ...state,
                items: state.items.map((item) => ({
                    ...item,
                    kotPrinted: true,
                    kotPrintedQuantity: item.qty || 0
                }))
            };
        case "SYNC_KOT_STATUS": {
            const unprintedMap = action.unprintedMap || new Map();
            return {
                ...state,
                items: state.items.map((item) => {
                    const key = getLineKey(item);
                    const pendingQty = Number(unprintedMap.get(key) || 0);
                    const isPrinted = pendingQty <= 0;
                    return {
                        ...item,
                        kotPrinted: isPrinted,
                        kotPrintedQuantity: isPrinted ? (item.qty || 0) : Math.max(0, (item.qty || 0) - pendingQty)
                    };
                })
            };
        }
        case "BUSY":
            return { ...state, busy: action.value };
        default:
            return state;
    }
}

// ---- Component ----
export default function RestaurantPOSForm() {

    const dispatchRedux = useDispatch();
    const navigate = useNavigate();
    const { MENU, isLoading } = useSelector((state) => state.pos);
    const {
        employees,
        heldOrders,
        tables: backendTables,
        isLoading: isPosBusinessLoading,
        isError: isPosBusinessError,
        message: posBusinessMessage,
    } = useSelector((state) => state.posBusiness);
    const { counters: dispatchCounters } = useSelector((state) => state.dispatchCounters);
    const { counters: takeawayCounters } = useSelector((state) => state.takeawayCounters);
    const { activeDeals } = useSelector((state) => state.deals);
    const { user } = useSelector((state) => state.auth);
    const [openingGuard, setOpeningGuard] = useState({
        checking: true,
        allowed: false,
    });

    // Delivery zones state
    const [deliveryZones, setDeliveryZones] = useState([]);
    const [posOrderTypeSettings, setPosOrderTypeSettings] = useState(DEFAULT_POS_ORDER_TYPE_SETTINGS);
    const restaurantOrderModeButtons = (posOrderTypeSettings.restaurantOrderModeOrder || ['dinein', 'takeaway', 'delivery']).map((mode) => ({
        mode,
        label: mode === 'dinein' ? 'Dine-in' : (mode === 'takeaway' ? 'Takeaway' : 'Delivery'),
        icon: mode === 'dinein' ? <TableBar sx={{ mr: .5 }} /> : (mode === 'takeaway' ? <TakeoutDining sx={{ mr: .5 }} /> : <LocalShipping sx={{ mr: .5 }} />),
    }));
    const actionLock = useRef(false);
    const checkoutIdempotencyKeyRef = useRef(null);
    const directCashCheckoutRequested = useRef(false);
    const directCardCheckoutRequested = useRef(false);
    const cashPromptBypassNextCheckout = useRef(false);
    const noticeQueueRef = useRef([]);
    const [noticeDialog, setNoticeDialog] = useState({
        open: false,
        title: "Action Required",
        message: "",
        severity: "info",
    });
    const [printPreviewDialog, setPrintPreviewDialog] = useState({
        open: false,
        documents: [],
        activeIndex: 0,
    });

    const showPrintPreviewDocuments = (documents = []) => {
        if (!Array.isArray(documents) || documents.length === 0) return;
        setPrintPreviewDialog({
            open: true,
            documents,
            activeIndex: 0,
        });
    };

    const closePrintPreviewDialog = () => {
        setPrintPreviewDialog({ open: false, documents: [], activeIndex: 0 });
    };

    const handlePrintPreviewTabChange = (_event, value) => {
        setPrintPreviewDialog((prev) => ({ ...prev, activeIndex: value }));
    };

    const buildPreviewPrintHtml = (documents = []) => {
        const body = documents.map((doc, index) => {
            const paperWidth = Number(doc.paperMm || 80);
            const pageBreak = index < documents.length - 1 ? ' page-break' : '';
            return `
                <section class="receipt${pageBreak}" style="width:${paperWidth}mm">
                    <div class="receipt-title">${escapeReceiptHtml(doc.title || 'Print Preview')}</div>
                    ${doc.sourceLabel ? `<div class="receipt-source">${escapeReceiptHtml(doc.sourceLabel)}</div>` : ''}
                    <pre>${escapeReceiptHtml(doc.plainText || '')}</pre>
                </section>
            `;
        }).join('');

        return `<!doctype html>
<html>
<head>
    <title>Receipt Print</title>
    <style>
        @page { margin: 4mm; }
        body { margin: 0; background: #fff; color: #000; }
        .receipt { box-sizing: border-box; margin: 0 auto; padding: 0; color: #000; }
        .receipt-title { text-align: center; font-weight: 700; margin-bottom: 4px; font-family: Arial, sans-serif; }
        .receipt-source { text-align: center; font-size: 11px; margin-bottom: 6px; font-family: Arial, sans-serif; }
        pre { margin: 0; white-space: pre-wrap; font-family: Consolas, 'Courier New', monospace; font-size: 11px; line-height: 1.25; }
        .page-break { page-break-after: always; }
    </style>
</head>
<body>${body}</body>
</html>`;
    };

    const printPreviewDocuments = (scope = 'selected') => {
        const documents = printPreviewDialog.documents || [];
        const selected = documents[printPreviewDialog.activeIndex] ? [documents[printPreviewDialog.activeIndex]] : [];
        const docsToPrint = scope === 'all' ? documents : selected;
        if (docsToPrint.length === 0) return;

        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';
        printFrame.style.visibility = 'hidden';
        document.body.appendChild(printFrame);

        const frameDocument = printFrame.contentWindow?.document;
        if (!frameDocument) {
            document.body.removeChild(printFrame);
            alert('Print preview could not be prepared. Please try again.');
            return;
        }

        const cleanup = () => {
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
        };

        frameDocument.open();
        frameDocument.write(buildPreviewPrintHtml(docsToPrint));
        frameDocument.close();

        setTimeout(() => {
            const frameWindow = printFrame.contentWindow;
            if (!frameWindow) {
                cleanup();
                return;
            }

            frameWindow.focus();
            frameWindow.print();
            setTimeout(cleanup, 1000);
        }, 250);
    };

    const acknowledgeNotice = () => {
        setNoticeDialog((current) => {
            const nextNotice = noticeQueueRef.current.shift();
            if (nextNotice) {
                return {
                    ...nextNotice,
                    open: true,
                };
            }

            return {
                ...current,
                open: false,
                message: "",
            };
        });
    };

    useEffect(() => {
        let isMounted = true;

        if (!user?.userID) {
            setOpeningGuard({ checking: false, allowed: false });
            navigate('/Login', { replace: true });
            return () => {
                isMounted = false;
            };
        }

        setOpeningGuard({ checking: true, allowed: false });
        dispatchRedux(getLastOpenTill({ userID: user.userID }))
            .unwrap()
            .then((response) => {
                if (!isMounted) return;
                if (response?.data?.openingID) {
                    setOpeningGuard({ checking: false, allowed: true });
                    return;
                }

                setOpeningGuard({ checking: false, allowed: false });
                navigate('/DailyOpening', { replace: true });
            })
            .catch(() => {
                if (!isMounted) return;
                setOpeningGuard({ checking: false, allowed: false });
                navigate('/DailyOpening', { replace: true });
            });

        return () => {
            isMounted = false;
        };
    }, [dispatchRedux, navigate, user?.userID]);
    useEffect(() => {
        setPosNoticeHandler((incomingNotice) => {
            setNoticeDialog((current) => {
                if (!current.open) {
                    return {
                        ...incomingNotice,
                        open: true,
                    };
                }

                noticeQueueRef.current.push(incomingNotice);
                return current;
            });
        });

        return () => {
            setPosNoticeHandler(null);
            noticeQueueRef.current = [];
        };
    }, []);

    // Set default branch from logged-in user
    useEffect(() => {
        if (user && user.defaultBranchID && state.branchID !== user.defaultBranchID) {
            dispatch({ type: "SET", payload: { branchID: user.defaultBranchID } });
        }
    }, [user]);

    useEffect(() => {
        if (!openingGuard.allowed) return;

        dispatchRedux(getItemsForPOS({ procOrder: "Get Items For POS", userID: user?.userID }))
        dispatchRedux(getEmployees())
        dispatchRedux(getHeldOrders())
        dispatchRedux(getTables()) // Fetch table status from backend
        dispatchRedux(fetchDispatchCounters())
        dispatchRedux(fetchTakeawayCounters())
        dispatchRedux(fetchActiveDeals())
        fetchPosOrderTypeSettings()
            .then((loadedSettings) => {
                setPosOrderTypeSettings(loadedSettings);
                const enabledByMode = {
                    dinein: loadedSettings.enableDineIn,
                    takeaway: loadedSettings.enableTakeaway,
                    delivery: loadedSettings.enableDelivery,
                };
                const defaultOrderType = (loadedSettings.restaurantOrderModeOrder || ['dinein', 'takeaway', 'delivery'])
                    .find((mode) => enabledByMode[mode]) || 'takeaway';
                if (!state.currentOrderID && !state.foodpanda.enabled && state.items.length === 0) {
                    dispatch({ type: 'SET', payload: { orderType: defaultOrderType } });
                }
            })
            .catch((error) => console.error('Error loading POS order type settings:', error))

        // Fetch delivery zones
        axios.get(`${global.SET_API_URL}delivery/zones`)
            .then(response => {
                if (response.data.success) {
                    setDeliveryZones(response.data.data);
                    console.log('✅ Loaded delivery zones:', response.data.data);
                }
            })
            .catch(error => {
                console.error('❌ Error fetching delivery zones:', error);
            });
    }, [dispatchRedux, openingGuard.allowed, user?.userID]);

    useEffect(() => {
        if (!openingGuard.allowed || !user?.userID) return undefined;

        let dailyRefresh;
        const now = new Date();
        const nextMidnight = new Date(now);
        nextMidnight.setHours(24, 0, 1, 0);

        const refreshMenuPricing = () => {
            dispatchRedux(getItemsForPOS({ procOrder: "Get Items For POS", userID: user.userID }));
        };

        const midnightRefresh = setTimeout(() => {
            refreshMenuPricing();
            dailyRefresh = setInterval(refreshMenuPricing, 24 * 60 * 60 * 1000);
        }, nextMidnight.getTime() - now.getTime());

        return () => {
            clearTimeout(midnightRefresh);
            if (dailyRefresh) clearInterval(dailyRefresh);
        };
    }, [dispatchRedux, openingGuard.allowed, user?.userID]);

    useEffect(() => {
        if (!openingGuard.allowed || !user?.userID) return undefined;

        const refreshMenuPricing = () => {
            dispatchRedux(getItemsForPOS({ procOrder: "Get Items For POS", userID: user.userID }));
        };
        const refreshWhenVisible = () => {
            if (document.visibilityState === "visible") refreshMenuPricing();
        };

        window.addEventListener("focus", refreshMenuPricing);
        document.addEventListener("visibilitychange", refreshWhenVisible);
        const periodicRefresh = setInterval(refreshMenuPricing, 60 * 1000);

        return () => {
            window.removeEventListener("focus", refreshMenuPricing);
            document.removeEventListener("visibilitychange", refreshWhenVisible);
            clearInterval(periodicRefresh);
        };
    }, [dispatchRedux, openingGuard.allowed, user?.userID]);

    const CATEGORIES = [
        "All",
        "Deals",
        ...Array.from(new Set(MENU && MENU.map((m) => m.category))),
    ];

    const [state, dispatch] = useReducer(reducer, initialState);
    const [companyEmployees, setCompanyEmployees] = useState([]);
    const [category, setCategory] = useState("All");

    useEffect(() => {
        axios.get(`${global.SET_API_URL}employees`, {
            params: { cmpID: user?.cmpID || 1 }
        })
            .then((response) => {
                setCompanyEmployees(
                    (response.data.employees || []).filter((employee) => employee.status === 'Active')
                );
            })
            .catch((error) => {
                console.error('Unable to load employees for POS:', error);
                setCompanyEmployees([]);
            });
    }, [user?.cmpID]);
    const [q, setQ] = useState("");
    const [tab, setTab] = useState(0);
    const [showTableModal, setShowTableModal] = useState(false);
    const [showHeldOrders, setShowHeldOrders] = useState(false);
    const [showDraftOrders, setShowDraftOrders] = useState(false);
    const [draftOrders, setDraftOrders] = useState([]);
    const [showDeliveryCashDialog, setShowDeliveryCashDialog] = useState(false);
    const [deliveryCashStatus, setDeliveryCashStatus] = useState('pending');
    const [deliveryCashSearch, setDeliveryCashSearch] = useState('');
    const [deliveryCashRows, setDeliveryCashRows] = useState([]);
    const [deliveryCashSummary, setDeliveryCashSummary] = useState({
        pendingOrders: 0, pendingAmount: 0, collectedOrders: 0, collectedAmount: 0
    });
    const [deliveryCashLoading, setDeliveryCashLoading] = useState(false);
    const [deliveryCashConfirmOrder, setDeliveryCashConfirmOrder] = useState(null);
    const [deliveryCashCollecting, setDeliveryCashCollecting] = useState(false);
    const [deliveryCashActionError, setDeliveryCashActionError] = useState('');
    const [showBillReprintModal, setShowBillReprintModal] = useState(false);
    const [showCashCheckoutDialog, setShowCashCheckoutDialog] = useState(false);
    const [showVariantDialog, setShowVariantDialog] = useState(false);
    const [variantDialogItem, setVariantDialogItem] = useState({
        baseName: "",
        category: "",
        options: []
    });
    const [cashReceivedAmount, setCashReceivedAmount] = useState(0);
    const [currentOrderIdempotencyKey, setCurrentOrderIdempotencyKey] = useState(null);
    const [reprintBills, setReprintBills] = useState([]);
    const [reprintActiveOrders, setReprintActiveOrders] = useState([]);
    const [reprintBillsLoading, setReprintBillsLoading] = useState(false);
    const [reprintOpeningDate, setReprintOpeningDate] = useState('');
    const [reprintUsers, setReprintUsers] = useState([]);
    const [reprintSelectedUserID, setReprintSelectedUserID] = useState('');
    const [reprintSearch, setReprintSearch] = useState('');
    const [reprintPaymentMethodFilter, setReprintPaymentMethodFilter] = useState('all');
    const [showReprintOrderViewModal, setShowReprintOrderViewModal] = useState(false);
    const [reprintOrderDetailLoading, setReprintOrderDetailLoading] = useState(false);

    useEffect(() => {
        if (!showTableModal) return;

        // Always refresh table status when modal opens so stale/failed initial fetch can recover.
        dispatchRedux(getTables());
        dispatchRedux(getHeldOrders());
    }, [showTableModal, dispatchRedux]);
    const [reprintOrderDetailError, setReprintOrderDetailError] = useState('');
    const [reprintOrderDetail, setReprintOrderDetail] = useState(null);
    const [reprintOrderSource, setReprintOrderSource] = useState('completed');
    const [kotStatuses, setKotStatuses] = useState({}); // {orderID: {printedItems, totalItems, kotPrinted}}

    const loadDeliveryCashOrders = async (nextStatus = deliveryCashStatus, nextSearch = deliveryCashSearch) => {
        setDeliveryCashLoading(true);
        setDeliveryCashActionError('');
        try {
            const branchID = state.branchID || user?.defaultBranchID || user?.branchID || 0;
            const response = await axios.get(`${global.SET_API_URL}delivery/cash-orders`, {
                params: { branchID, status: nextStatus, search: nextSearch },
            });
            setDeliveryCashRows(response.data?.data || []);
            setDeliveryCashSummary(response.data?.summary || {
                pendingOrders: 0, pendingAmount: 0, collectedOrders: 0, collectedAmount: 0
            });
        } catch (error) {
            setDeliveryCashRows([]);
            setDeliveryCashActionError(error?.response?.data?.message || 'Delivery cash orders load nahi ho sake.');
        } finally {
            setDeliveryCashLoading(false);
        }
    };

    const openDeliveryCashDialog = async () => {
        setDeliveryCashStatus('pending');
        setDeliveryCashSearch('');
        setShowDeliveryCashDialog(true);
        await loadDeliveryCashOrders('pending', '');
    };

    const changeDeliveryCashStatus = async (status) => {
        setDeliveryCashStatus(status);
        await loadDeliveryCashOrders(status, deliveryCashSearch);
    };

    const openDeliveryCashConfirmation = (order) => {
        setDeliveryCashActionError('');
        setDeliveryCashConfirmOrder(order);
    };

    const closeDeliveryCashConfirmation = () => {
        if (deliveryCashCollecting) return;
        setDeliveryCashConfirmOrder(null);
        setDeliveryCashActionError('');
    };

    const markDeliveryCashCollected = async () => {
        const order = deliveryCashConfirmOrder;
        if (!order || deliveryCashCollecting) return;
        setDeliveryCashCollecting(true);
        setDeliveryCashActionError('');
        try {
            await axios.post(`${global.SET_API_URL}delivery/cash-orders/${order.orderID}/collect`, {
                changedBy: user?.userID,
                remarks: 'Collected from POS Delivery Cash',
            });
            setDeliveryCashConfirmOrder(null);
            await loadDeliveryCashOrders(deliveryCashStatus, deliveryCashSearch);
        } catch (error) {
            setDeliveryCashActionError(error?.response?.data?.message || 'Delivery cash collect nahi ho saka.');
        } finally {
            setDeliveryCashCollecting(false);
        }
    };

    const openVariantDialog = (menuCard) => {
        setVariantDialogItem({
            baseName: menuCard.name,
            category: menuCard.category,
            options: menuCard.variantOptions || []
        });
        setShowVariantDialog(true);
    };

    const closeVariantDialog = () => {
        setShowVariantDialog(false);
        setVariantDialogItem({ baseName: "", category: "", options: [] });
    };

    const handleVariantSelect = (variantItem) => {
        dispatch({ type: "ADD_ITEM", item: variantItem });
        closeVariantDialog();
    };
    useEffect(() => {
        const fetchKotStatuses = async () => {
            if (!heldOrders || heldOrders.length === 0) {
                setKotStatuses({});
                return;
            }

            const updates = {};
            try {
                await Promise.all(heldOrders.map(async (order) => {
                    try {
                        const resp = await axios.get(`${global.SET_API_URL}kot/getOrderKotStatus/${order.orderID}`);
                        if (resp.data && resp.data.success) {
                            updates[order.orderID] = {
                                printedItems: resp.data.printedItems,
                                totalItems: resp.data.totalItems,
                                kotPrinted: resp.data.kotPrinted
                            };
                        }
                    } catch (err) {
                        // Ignore individual failures
                    }
                }));
                setKotStatuses(updates);
            } catch (e) {
                // Ignore batch failure
            }
        };

        fetchKotStatuses();
    }, [heldOrders]);

    // Ensure held orders refresh when opening the dialog (e.g., after page refresh)
    useEffect(() => {
        if (showHeldOrders) {
            dispatchRedux(getHeldOrders())
        }
    }, [showHeldOrders, dispatchRedux]);

    useEffect(() => {
        const fetchDraftOrders = async () => {
            if (!user?.userID) return;
            try {
                const response = await axios.get(`${global.SET_API_URL}posBusiness/getDraftOrders/${user.userID}`);
                if (response.data?.success) {
                    setDraftOrders(response.data.draftOrders || []);
                } else {
                    setDraftOrders([]);
                }
            } catch (error) {
                console.error('Error fetching draft orders:', error);
                setDraftOrders([]);
            }
        };

        fetchDraftOrders();
    }, [showDraftOrders, user?.userID]);
    const [now, setNow] = useState(Date.now());
    const totals = useMemo(() => calcTotals(state), [state]);
    const [billingLink, setBillingLink] = useState(null);
    const [taxModeEnabled, setTaxModeEnabled] = useState(false);
    const activeBillingName = user?.dispName || user?.userName;

    useEffect(() => {
        if (!user?.userID) return;
        setTaxModeEnabled(false);
        axios.get(global.SET_API_URL + 'billingIdentities/current/' + user.userID)
            .then((response) => setBillingLink(response.data?.data || null))
            .catch(() => setBillingLink(null));
    }, [user?.userID]);

    useEffect(() => {
        dispatch({ type: "SET", payload: { applyTax: taxModeEnabled, taxRate: taxModeEnabled ? Number(billingLink?.taxRate || 0) : 0 } });
    }, [taxModeEnabled, billingLink?.taxRate]);

    const toggleBillingIdentity = () => {
        if (state.busy) return;
        setTaxModeEnabled((current) => !current);
    };

    const hasPendingKotInHeldOrders = useMemo(() => {
        if (!heldOrders || heldOrders.length === 0) return false;

        return heldOrders.some((order) => {
            const status = kotStatuses[order.orderID];
            if (!status) return false;

            const printedItems = Number(status.printedItems || 0);
            const totalItems = Number(status.totalItems || 0);
            return !status.kotPrinted || printedItems < totalItems;
        });
    }, [heldOrders, kotStatuses]);

    const sortedHeldOrders = useMemo(() => {
        if (!heldOrders || heldOrders.length === 0) return [];

        return heldOrders
            .map((order, index) => {
                const status = kotStatuses[order.orderID];
                const printedItems = Number(status?.printedItems || 0);
                const totalItems = Number(status?.totalItems || 0);
                const hasPendingKot = !!status && (!status.kotPrinted || printedItems < totalItems);

                return { order, index, hasPendingKot };
            })
            .sort((left, right) => {
                if (left.hasPendingKot === right.hasPendingKot) {
                    return left.index - right.index;
                }
                return left.hasPendingKot ? -1 : 1;
            })
            .map((row) => row.order);
    }, [heldOrders, kotStatuses]);

    const extractReprintPaymentMethods = (entry) => {
        return String(entry?.paymentMethods || '')
            .split(',')
            .map((method) => method.trim())
            .filter(Boolean);
    };

    const reprintPaymentMethodOptions = useMemo(() => {
        const allRows = [...reprintBills, ...reprintActiveOrders];
        const methodMap = new Map();

        allRows.forEach((row) => {
            extractReprintPaymentMethods(row).forEach((method) => {
                const key = method.toLowerCase();
                if (!methodMap.has(key)) {
                    methodMap.set(key, method);
                }
            });
        });

        return Array.from(methodMap.entries())
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([value, label]) => ({ value, label }));
    }, [reprintBills, reprintActiveOrders]);

    const matchesReprintSearch = (entry, search) => {
        const orderId = String(entry.orderID || '').toLowerCase();
        const kotRef = String(entry.kotNumber || entry.kotNo || '').toLowerCase();
        const paymentMethodsRaw = String(entry.paymentMethods || '').toLowerCase();
        const amountValue = Number(entry.grandTotal ?? entry.totalAmount ?? 0);
        const amount = Number.isFinite(amountValue) ? amountValue : 0;
        const amountRaw = String(amount).toLowerCase();
        const amountRounded = String(Math.round(amount)).toLowerCase();
        const amountFixed = amount.toFixed(2).toLowerCase();
        const amountFormatted = amount.toLocaleString('en-US').toLowerCase();

        return (
            orderId.includes(search) ||
            kotRef.includes(search) ||
            paymentMethodsRaw.includes(search) ||
            amountRaw.includes(search) ||
            amountRounded.includes(search) ||
            amountFixed.includes(search) ||
            amountFormatted.includes(search)
        );
    };

    const filteredReprintBills = useMemo(() => {
        const search = String(reprintSearch || '').trim().toLowerCase();
        return reprintBills.filter((bill) => {
            const searchMatched = !search || matchesReprintSearch(bill, search);
            const paymentMatched =
                !reprintPaymentMethodFilter ||
                reprintPaymentMethodFilter === 'all' ||
                extractReprintPaymentMethods(bill)
                    .map((method) => method.toLowerCase())
                    .includes(reprintPaymentMethodFilter);
            return searchMatched && paymentMatched;
        });
    }, [reprintBills, reprintSearch, reprintPaymentMethodFilter]);

    const filteredReprintActiveOrders = useMemo(() => {
        const search = String(reprintSearch || '').trim().toLowerCase();
        return reprintActiveOrders.filter((order) => {
            const searchMatched = !search || matchesReprintSearch(order, search);
            const paymentMatched =
                !reprintPaymentMethodFilter ||
                reprintPaymentMethodFilter === 'all' ||
                extractReprintPaymentMethods(order)
                    .map((method) => method.toLowerCase())
                    .includes(reprintPaymentMethodFilter);
            return searchMatched && paymentMatched;
        });
    }, [reprintActiveOrders, reprintSearch, reprintPaymentMethodFilter]);

    const viewedReprintOrder = reprintOrderDetail?.order || null;
    const viewedReprintOrderStatus = String(viewedReprintOrder?.orderStatus || '').toLowerCase();
    const canPrintViewedFinalBill = viewedReprintOrderStatus === 'completed' || reprintOrderSource === 'completed';

    const thermalPreviewText = useMemo(() => {
        if (!viewedReprintOrder) return '';

        const viewedReprintItems = reprintOrderDetail?.items || [];
        const viewedReprintPayments = reprintOrderDetail?.payments || [];

        const width = THERMAL_PREVIEW_WIDTH;
        const divider = '='.repeat(width);
        const lightDivider = '-'.repeat(width);
        const lines = [];

        const normalizedOrderType = String(viewedReprintOrder.orderType || '').toLowerCase();
        const isFoodpandaOrder = String(viewedReprintOrder.aggregatorPlatform || '').toLowerCase() === 'foodpanda';

        let title = 'COMPLETE ORDER';
        if (normalizedOrderType === 'takeaway') title = 'TAKEAWAY ORDER';
        if (normalizedOrderType === 'dispatch') title = 'DISPATCH ORDER';
        if (normalizedOrderType === 'delivery') {
            title = isFoodpandaOrder ? 'FOODPANDA ORDER' : 'DELIVERY ORDER';
        }

        lines.push(thermalCenter(title, width));
        lines.push(divider);

        lines.push(thermalLeftRight('Order #', viewedReprintOrder.orderID || 'N/A', width));
        lines.push(
            thermalLeftRight(
                'KOT #',
                viewedReprintOrder.kotNumber || viewedReprintOrder.lastKotNo || viewedReprintOrder.firstKotNo || 'N/A',
                width
            )
        );
        lines.push(thermalLeftRight('Type', String(viewedReprintOrder.orderType || 'N/A').toUpperCase(), width));

        if (viewedReprintOrder.tableLabel) {
            lines.push(thermalLeftRight('Table', viewedReprintOrder.tableLabel, width));
        }
        if (reprintOrderDetail?.latestHold?.ticketNo) {
            lines.push(thermalLeftRight('Ticket', reprintOrderDetail.latestHold.ticketNo, width));
        }
        if (viewedReprintOrder.waiterName) {
            lines.push(thermalLeftRight('Order Taker', viewedReprintOrder.waiterName, width));
        }
        if (viewedReprintOrder.riderName) {
            lines.push(thermalLeftRight('Rider', viewedReprintOrder.riderName, width));
        }
        if (viewedReprintOrder.customerName) {
            lines.push(thermalLeftRight('Customer', viewedReprintOrder.customerName, width));
        }
        if (viewedReprintOrder.customerPhone) {
            lines.push(thermalLeftRight('Phone', viewedReprintOrder.customerPhone, width));
        }
        if (isFoodpandaOrder && viewedReprintOrder.aggregatorOrderCode) {
            lines.push(thermalLeftRight('FP Order', viewedReprintOrder.aggregatorOrderCode, width));
        }

        lines.push(
            thermalLeftRight(
                'Created',
                viewedReprintOrder.createdAt ? new Date(viewedReprintOrder.createdAt).toLocaleString() : 'N/A',
                width
            )
        );
        if (viewedReprintOrder.completedAt) {
            lines.push(
                thermalLeftRight(
                    'Completed',
                    new Date(viewedReprintOrder.completedAt).toLocaleString(),
                    width
                )
            );
        }

        lines.push(lightDivider);
        lines.push('ITEMS');
        lines.push(lightDivider);

        if (viewedReprintItems.length === 0) {
            lines.push('No items found');
        } else {
            viewedReprintItems.forEach((item) => {
                const qty = Number(item.quantity || 0);
                const unitPrice = Number(item.unitPrice || item.price || 0);
                const lineDiscount = Number(item.lineDiscount || 0);
                const itemTotal = Number(item.itemTotal ?? (qty * unitPrice));
                const itemName = item.itemName || 'Item';
                const qtyRateAmount = `${qty}x${unitPrice.toLocaleString()}=${itemTotal.toLocaleString()}`;

                lines.push(thermalLeftRight(itemName, qtyRateAmount, width));

                if (lineDiscount > 0) {
                    lines.push(thermalLeftRight('  Discount', `-${lineDiscount.toLocaleString()}`, width));
                }

                if (item.notes) {
                    thermalWrap(`Note: ${item.notes}`, width - 2).forEach((noteLine) => {
                        lines.push(`  ${noteLine}`);
                    });
                }

                lines.push(lightDivider);
            });

            if (lines[lines.length - 1] === lightDivider) {
                lines.pop();
            }
        }

        lines.push(divider);
        const reprintGrossSubtotal = viewedReprintItems.reduce(
            (sum, item) => sum + (Number(item.unitPrice || item.price || 0) * Number(item.quantity || item.qty || 0)),
            0
        );
        const reprintLineDiscount = viewedReprintItems.reduce(
            (sum, item) => sum + Number(item.lineDiscount || 0),
            0
        );
        lines.push(thermalLeftRight('Sub Total', thermalMoney(reprintGrossSubtotal), width));
        if (reprintLineDiscount > 0) lines.push(thermalLeftRight('Discount', `-${thermalMoney(reprintLineDiscount)}`, width));

        if (Number(viewedReprintOrder.serviceCharge || 0) > 0) {
            lines.push(thermalLeftRight('Service Charge', thermalMoney(viewedReprintOrder.serviceCharge), width));
        }
        if (Number(viewedReprintOrder.taxAmount || 0) > 0) {
            lines.push(thermalLeftRight('Tax', thermalMoney(viewedReprintOrder.taxAmount), width));
        }
        if (Number(viewedReprintOrder.deliveryFee || 0) > 0) {
            lines.push(thermalLeftRight('Delivery Fee', thermalMoney(viewedReprintOrder.deliveryFee), width));
        }
        if (Number(viewedReprintOrder.couponAmount || 0) > 0) {
            lines.push(thermalLeftRight('Coupon', `-${thermalMoney(viewedReprintOrder.couponAmount)}`, width));
        }
        if (Number(viewedReprintOrder.overallDiscount || 0) > 0) {
            lines.push(thermalLeftRight('Discount', `-${thermalMoney(viewedReprintOrder.overallDiscount)}`, width));
        }

        lines.push(lightDivider);
        lines.push(thermalLeftRight('GRAND TOTAL', thermalMoney(viewedReprintOrder.grandTotal), width));
        lines.push(divider);

        if (viewedReprintPayments.length > 0) {
            lines.push('PAYMENTS');
            lines.push(lightDivider);
            viewedReprintPayments.forEach((payment) => {
                lines.push(
                    thermalLeftRight(
                        String(payment.paymentMethod || 'PAYMENT').toUpperCase(),
                        thermalMoney(payment.amount),
                        width
                    )
                );
            });
            lines.push(divider);
        }

        if (viewedReprintOrder.customerAddress) {
            lines.push('ADDRESS');
            lines.push(lightDivider);
            thermalWrap(viewedReprintOrder.customerAddress, width).forEach((addressLine) => {
                lines.push(addressLine);
            });
            lines.push(divider);
        }

        if (viewedReprintOrder.orderRemarks) {
            lines.push('REMARKS');
            lines.push(lightDivider);
            thermalWrap(viewedReprintOrder.orderRemarks, width).forEach((remarkLine) => {
                lines.push(remarkLine);
            });
            lines.push(divider);
        }

        lines.push(thermalCenter('PREVIEW ONLY', width));

        return lines.join('\n');
    }, [viewedReprintOrder, reprintOrderDetail]);

    const isItemKotPrinted = (item) => {
        const qty = Number(item?.qty || 0);
        const printedQty = Number(item?.kotPrintedQuantity || 0);
        return printedQty >= qty || item?.kotPrinted === true || Number(item?.kotPrinted) === 1;
    };

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const effectiveOrderType = state.foodpanda.enabled ? "delivery" : state.orderType;
    const isFoodpandaOrder = state.foodpanda.enabled;

    const isOrderModeEnabled = (mode) => {
        if (mode === 'dinein') return Boolean(posOrderTypeSettings.enableDineIn);
        if (mode === 'takeaway') return Boolean(posOrderTypeSettings.enableTakeaway);
        if (mode === 'delivery') return Boolean(posOrderTypeSettings.enableDelivery);
        if (mode === 'foodpanda') return Boolean(posOrderTypeSettings.enableFoodpanda);
        return true;
    };

    const getFirstEnabledOrderType = () => {
        return (posOrderTypeSettings.restaurantOrderModeOrder || ['dinein', 'takeaway', 'delivery'])
            .find((mode) => isOrderModeEnabled(mode)) || 'takeaway';
    };

    useEffect(() => {
        const payload = {};
        if (state.foodpanda.enabled && !isOrderModeEnabled('foodpanda')) {
            payload.foodpanda = { ...state.foodpanda, enabled: false };
        }

        if (!isOrderModeEnabled(state.orderType)) {
            payload.orderType = getFirstEnabledOrderType();
            payload.dispatchCounterId = '';
            payload.takeawayCounterId = '';
        }

        if (Object.keys(payload).length) {
            dispatch({ type: "SET", payload });
        }
    }, [posOrderTypeSettings, state.orderType, state.foodpanda]);

    // Force Foodpanda orders to behave like delivery + prepaid
    useEffect(() => {
        if (isFoodpandaOrder) {
            const payload = {};
            if (state.orderType !== 'delivery') payload.orderType = 'delivery';
            if (state.deliveryPaymentMethod !== 'prepaid') payload.deliveryPaymentMethod = 'prepaid';
            if (Object.keys(payload).length) {
                dispatch({ type: "SET", payload });
            }
        }
    }, [isFoodpandaOrder, state.orderType, state.deliveryPaymentMethod]);

    // Auto-select dispatch counter if only 1 exists
    useEffect(() => {
        if (dispatchCounters && dispatchCounters.length === 1 && state.orderType === 'dinein' && !state.dispatchCounterId) {
            dispatch({ type: "SET", payload: { dispatchCounterId: dispatchCounters[0].dispatchCounterId } });
        }
    }, [dispatchCounters, state.orderType, state.dispatchCounterId]);

    // Auto-select takeaway counter if only 1 exists (takeaway + delivery)
    useEffect(() => {
        if (
            takeawayCounters &&
            takeawayCounters.length === 1 &&
            (state.orderType === 'takeaway' || state.orderType === 'delivery') &&
            !state.takeawayCounterId
        ) {
            dispatch({ type: "SET", payload: { takeawayCounterId: takeawayCounters[0].takeawayCounterId } });
        }
    }, [takeawayCounters, state.orderType, state.takeawayCounterId]);

    const formatElapsed = (assignedAt) => {
        if (!assignedAt) return "";
        const totalMinutes = Math.max(0, Math.floor((now - assignedAt) / 60000));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}:${String(minutes).padStart(2, '0')}`;
    };

    const getTableColor = (assignedAt) => {
        if (!assignedAt) return { bg: '#e8f5e9', color: '#2e7d32' }; // free - green
        const mins = (now - assignedAt) / 60000;
        if (mins < 1) return { bg: '#e3f2fd', color: '#1565c0' }; // blue (<10m)
        if (mins < 2) return { bg: '#fff9c4', color: '#f9a825' }; // yellow (10-20m)
        if (mins < 3) return { bg: '#ffe0b2', color: '#ef6c00' }; // orange (20-30m)
        return { bg: '#ffebee', color: '#c62828' }; // red (>=30m)
    };

    // Helper function to group deal items for bill display
    const groupItemsForBill = (items) => {
        const dealGroups = {};
        const regularItems = [];

        items.forEach(item => {
            if (item.isDealItem) {
                // Group by dealID
                if (!dealGroups[item.dealID]) {
                    const bundleQty = getDealBundleQty(item);
                    const baseQty = getDealItemBaseQty(item);
                    dealGroups[item.dealID] = {
                        name: item.dealName,
                        dealID: item.dealID,
                        qty: bundleQty,
                        qtyLabel: String(bundleQty),
                        // Find the item with full price (first item)
                        price: item.price > 0 ? item.price * baseQty : 0,
                        isDeal: true,
                        lineDiscount: 0,
                        note: null,
                        components: []
                    };
                } else {
                    // If price is set on this item, use it
                    if (item.price > 0) {
                        dealGroups[item.dealID].price = item.price * getDealItemBaseQty(item);
                    }
                }

                dealGroups[item.dealID].components.push({
                    name: item.name,
                    qtyLabel: formatQtyWithUnit(item.qty, item.baseUnitName)
                });
            } else {
                // Regular items as-is
                regularItems.push({
                    id: item.itemCode || item.id,
                    name: item.name,
                    qty: item.qty,
                    price: item.price,
                    lineDiscount: item.lineDiscount || 0,
                    note: item.note || null
                });
            }
        });

        // Convert deal groups to array
        const dealItems = Object.values(dealGroups).map(deal => ({
            name: deal.name,
            qty: deal.qty,
            price: deal.price,
            isDeal: true,
            dealID: deal.dealID,
            qtyLabel: deal.qtyLabel,
            components: deal.components,
            lineDiscount: 0,
            note: null
        }));

        return [...dealItems, ...regularItems];
    };

    const cartDisplayItems = useMemo(() => buildGroupedCartItems(state.items), [state.items]);

    const filteredMenu = useMemo(() => {
        if (category === "Deals") {
            if (!activeDeals || activeDeals.length === 0) {
                console.log('No active deals available');
                return [];
            }
            console.log('Active deals:', activeDeals);
            // Transform deals to menu format
            return activeDeals
                .filter(deal =>
                    q.trim() === "" ||
                    deal.dealName.toLowerCase().includes(q.toLowerCase())
                )
                .map(deal => {
                    let dealItems = [];
                    try {
                        dealItems = deal.items ? JSON.parse(deal.items) : [];
                    } catch (e) {
                        console.error('Failed to parse deal items for deal:', deal.dealID, e);
                    }
                    return {
                        id: `deal-${deal.dealID}`,
                        name: deal.dealName,
                        price: parseFloat(deal.dealPrice),
                        category: "Deals",
                        itemImage: deal.dealImage || "default-deal.png",
                        isDeal: true,
                        dealID: deal.dealID,
                        dealItems: dealItems,
                        dealDescription: deal.dealDescription
                    };
                });
        }

        if (!MENU) return [];

        return MENU.filter((m) =>
            (category === "All" || m.category === category) &&
            (q.trim() === "" || m.name.toLowerCase().includes(q.toLowerCase()))
        );
    }, [category, q, MENU, activeDeals]);

    const menuCards = useMemo(() => {
        if (!filteredMenu || filteredMenu.length === 0) return [];

        if (category === "Deals") {
            return filteredMenu.map((item) => ({
                id: item.id,
                name: item.name,
                category: item.category,
                priceText: currency(item.price),
                itemToAdd: item,
                isDeal: true,
                variantOptions: []
            }));
        }

        const grouped = new Map();

        filteredMenu.forEach((item) => {
            const itemVariantType = String(item.variantType || '').toLowerCase();
            const baseName = String(item.baseItemName || '').trim();
            const variantLabel = formatVariantLabel(item.variantValue || '');
            const hasStructuredVariant = itemVariantType === 'variant' && baseName && variantLabel;
            const isParentItem = itemVariantType === 'parent';

            if (!hasStructuredVariant && !isParentItem) {
                grouped.set(`single-${item.id}`, {
                    id: item.id,
                    name: item.name,
                    category: item.category,
                    priceText: currency(item.effectivePrice ?? item.price),
                    itemToAdd: item,
                    isDeal: false,
                    variantOptions: []
                });
                return;
            }

            const resolvedBaseName = hasStructuredVariant ? baseName : String(item.name || '').trim();
            const groupKey = `variant-${String(item.category || "").toLowerCase()}-${resolvedBaseName.toLowerCase()}`;
            if (!grouped.has(groupKey)) {
                grouped.set(groupKey, {
                    id: groupKey,
                    name: resolvedBaseName,
                    category: item.category,
                    isDeal: false,
                    variantOptions: [],
                    parentItem: null,
                });
            }

            const groupEntry = grouped.get(groupKey);

            if (isParentItem) {
                groupEntry.parentItem = item;
                return;
            }

            groupEntry.variantOptions.push({
                ...item,
                variantLabel,
            });
        });

        const cards = [];
        grouped.forEach((entry) => {
            if (!entry.variantOptions || entry.variantOptions.length === 0) {
                if (entry.parentItem) {
                    cards.push({
                        id: entry.parentItem.id,
                        name: entry.name,
                        category: entry.category,
                        priceText: currency(entry.parentItem.effectivePrice ?? entry.parentItem.price),
                        itemToAdd: entry.parentItem,
                        isDeal: false,
                        variantOptions: []
                    });
                    return;
                }

                cards.push(entry);
                return;
            }

            const sortedOptions = [...entry.variantOptions].sort((left, right) => {
                const rankDiff = getVariantSortRank(left.variantLabel) - getVariantSortRank(right.variantLabel);
                if (rankDiff !== 0) return rankDiff;
                return String(left.variantLabel || '').localeCompare(String(right.variantLabel || ''));
            });

            if (sortedOptions.length === 1) {
                const onlyOption = sortedOptions[0];
                cards.push({
                    id: onlyOption.id,
                    name: onlyOption.name,
                    category: onlyOption.category,
                    priceText: currency(onlyOption.effectivePrice ?? onlyOption.price),
                    itemToAdd: onlyOption,
                    isDeal: false,
                    variantOptions: []
                });
                return;
            }

            const numericPrices = sortedOptions
                .map((option) => Number(option.effectivePrice ?? option.price ?? 0))
                .filter((price) => Number.isFinite(price));
            const minPrice = numericPrices.length > 0 ? Math.min(...numericPrices) : 0;
            const maxPrice = numericPrices.length > 0 ? Math.max(...numericPrices) : 0;

            cards.push({
                id: entry.id,
                name: entry.name,
                category: entry.category,
                priceText: minPrice === maxPrice
                    ? currency(minPrice)
                    : `${currency(minPrice)} - ${currency(maxPrice)}`,
                itemToAdd: null,
                isDeal: false,
                variantOptions: sortedOptions
            });
        });

        return cards.sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));
    }, [filteredMenu, category]);

    const handleMenuCardClick = (menuCard) => {
        if (menuCard.variantOptions && menuCard.variantOptions.length > 1) {
            openVariantDialog(menuCard);
            return;
        }

        if (menuCard.itemToAdd) {
            dispatch({ type: "ADD_ITEM", item: menuCard.itemToAdd });
        }
    };

    const buildOrderPayload = () => {
        const paymentMethod = effectiveOrderType === 'delivery'
            ? (isFoodpandaOrder ? 'prepaid' : state.deliveryPaymentMethod)
            : null;

        const amountToCollect = isFoodpandaOrder
            ? 0
            : (effectiveOrderType === 'delivery' && state.deliveryPaymentMethod === 'cod' ? totals.grand : 0);

        const paymentsPayload = state.isEmployeeOrder
            ? []
            : isFoodpandaOrder
            ? [{ method: 'Card', amount: totals.grand }]
            : state.payments.filter(p => p.amount > 0).map(p => ({
                method: p.method,
                amount: Number(p.amount)
            }));

        return {
            userID: user?.userID || 1, // Till/accounting owner remains the logged-in operator
            loggedInUserID: user?.userID || 1,
            billingUserID: user?.userID || 1,
            taxModeEnabled,
            taxAuthority: taxModeEnabled ? billingLink?.taxAuthority : null,
            billingIdentityLinkID: billingLink?.linkID || null,
            branchID: state.branchID || user?.defaultBranchID || 1, // Get from state (which is set from user)
            tableLabel: state.tableNo || null,
            tableID: state.tableID || null,
            orderType: effectiveOrderType,
            isEmployeeOrder: state.isEmployeeOrder ? 1 : 0,
            employeeID: state.isEmployeeOrder ? state.employeeID || null : null,
            waiterID: state.waiterID || null,
            riderID: state.riderID || null,
            dispatchCounterId: state.dispatchCounterId || null,
            takeawayCounterId: state.takeawayCounterId || null,
            customerName: state.customer.name || null,
            customerPhone: state.customer.phone || null,
            customerAddress: state.customer.address || null,
            deliveryZoneId: effectiveOrderType === 'delivery' ? state.deliveryZoneId || null : null,
            deliveryStatus: effectiveOrderType === 'delivery' ? 'pending' : null,
            paymentMethod,
            amountToCollect,
            items: state.items
                .filter(item => !item.isDeal)
                .map(buildOrderItemPayload),
            payments: paymentsPayload,
            subTotal: totals.subTotal,
            serviceCharge: totals.service,
            taxAmount: totals.tax,
            deliveryFee: totals.delivery,
            couponCode: state.couponCode || null,
            couponAmount: totals.coupon,
            overallDiscount: totals.overallDiscount,
            grandTotal: totals.grand,
            remarks: state.notes || null,
            aggregatorPlatform: isFoodpandaOrder ? 'foodpanda' : null,
            aggregatorOrderCode: isFoodpandaOrder ? state.foodpanda.orderId || null : null,
            aggregatorStatus: isFoodpandaOrder ? state.foodpanda.status || null : null,
            aggregatorRider: isFoodpandaOrder ? state.foodpanda.rider || null : null,
            aggregatorCommissionPct: isFoodpandaOrder ? Number(state.foodpanda.commissionPct || 0) : null,
            aggregatorFee: isFoodpandaOrder ? Number(state.foodpanda.platformFee || 0) : null,
            idempotencyKey: currentOrderIdempotencyKey
        };
    };

    const canCheckout = state.items.length > 0 &&
        (state.orderType === "dinein" ||
            (state.orderType === "takeaway" && state.takeawayCounterId) ||
            (state.orderType === "dispatch" && state.dispatchCounterId) ||
            (state.orderType === "delivery" && (state.foodpanda.enabled || state.customer.phone)));

    const canProceedCheckout = canCheckout && (state.orderType !== "dinein" || !!state.tableNo);
    const canReprintBill = !!user?.userID && !state.busy;
    const nonCashPaidAmount = state.payments.reduce((sum, payment) => (
        String(payment.method || '').toLowerCase() === 'cash'
            ? sum
            : sum + (Number(payment.amount) || 0)
    ), 0);
    const cashAmountDue = Math.max(0, Number(totals.grand || 0) - nonCashPaidAmount);
    const cashChangeAmount = Math.max(0, Number(cashReceivedAmount || 0) - cashAmountDue);
    const cashRemainingDue = Math.max(0, cashAmountDue - Number(cashReceivedAmount || 0));

    // Get selected table info for display
    const selectedTable = useMemo(() => {
        if (!state.tableNo || !backendTables) return null;
        return backendTables.find(t => t.tableLabel === state.tableNo);
    }, [state.tableNo, backendTables]);

    const selectedTableTime = useMemo(() => {
        if (!selectedTable || !selectedTable.assignedAt) return null;
        return new Date(selectedTable.assignedAt).getTime();
    }, [selectedTable]);

    const handleSave = async () => {
        if (actionLock.current) return;
        actionLock.current = true;
        let busySet = false;

        try {
            dispatch({ type: "BUSY", value: true });
            busySet = true;
            let orderIDToHold;

            // Check if this is a resumed order
            if (state.currentOrderID) {
                // This is a resumed order - update existing order
                const updateData = {
                    orderID: state.currentOrderID,
                    items: state.items.map(buildOrderItemPayload),
                    payments: state.payments.filter(p => p.amount > 0).map(p => ({
                        method: p.method,
                        amount: Number(p.amount)
                    })),
                    subTotal: totals.subTotal,
                    serviceCharge: totals.service,
                    taxAmount: totals.tax,
                    deliveryFee: totals.delivery,
                    couponCode: state.couponCode || null,
                    couponAmount: totals.coupon,
                    overallDiscount: totals.overallDiscount,
                    grandTotal: totals.grand,
                    remarks: state.notes || null
                };

                await dispatchRedux(updateOrder(updateData)).unwrap();
                orderIDToHold = state.currentOrderID;
            } else {
                // This is a new order - save it first
                // Generate idempotency key for new order
                const newKey = generateOrderIdempotencyKey(user?.userID, state.orderType, state.tableNo);
                setCurrentOrderIdempotencyKey(newKey);
                
                const orderData = buildOrderPayload();
                orderData.orderStatus = 'parked';
                orderData.idempotencyKey = newKey;

                const orderResult = await dispatchRedux(saveOrder(orderData)).unwrap();
                orderIDToHold = orderResult.orderID;
            }

            // Create hold record
            const holdData = {
                orderID: orderIDToHold,
                ticketNo: `HOLD-${Date.now()}`,
                remarks: 'Held from POS'
            };

            await dispatchRedux(holdOrder(holdData)).unwrap();

            // Refresh held orders list
            await dispatchRedux(getHeldOrders()).unwrap();

            console.info(`Order parked successfully. Ticket: ${holdData.ticketNo}`);
            dispatch({ type: "CLEAR" });
            setCurrentOrderIdempotencyKey(null);
        } catch (error) {
            console.error('Error parking order:', error);
        } finally {
            if (busySet) {
                dispatch({ type: "BUSY", value: false });
            }
            actionLock.current = false;
        }
    };

    const handleSaveDraft = async () => {
        if (actionLock.current) return;
        actionLock.current = true;
        let busySet = false;

        if (state.items.length === 0) {
            alert('No items to save as draft!');
            actionLock.current = false;
            return;
        }

        try {
            dispatch({ type: "BUSY", value: true });
            busySet = true;

            if (state.currentOrderID) {
                const updateData = {
                    orderID: state.currentOrderID,
                    items: state.items.map(buildOrderItemPayload),
                    payments: state.payments.filter(p => p.amount > 0).map(p => ({
                        method: p.method,
                        amount: Number(p.amount)
                    })),
                    subTotal: totals.subTotal,
                    serviceCharge: totals.service,
                    taxAmount: totals.tax,
                    deliveryFee: totals.delivery,
                    couponCode: state.couponCode || null,
                    couponAmount: totals.coupon,
                    overallDiscount: totals.overallDiscount,
                    grandTotal: totals.grand,
                    remarks: state.notes || null,
                    orderStatus: 'draft'
                };

                await dispatchRedux(updateOrder(updateData)).unwrap();
            } else {
                // Generate idempotency key for new draft order
                const newKey = generateOrderIdempotencyKey(user?.userID, state.orderType, state.tableNo);
                setCurrentOrderIdempotencyKey(newKey);
                
                const orderData = {
                    ...buildOrderPayload(),
                    orderStatus: 'draft',
                    idempotencyKey: newKey
                };
                await dispatchRedux(saveOrder(orderData)).unwrap();
            }

            if (user?.userID) {
                try {
                    const response = await axios.get(`${global.SET_API_URL}posBusiness/getDraftOrders/${user.userID}`);
                    if (response.data?.success) {
                        setDraftOrders(response.data.draftOrders || []);
                    }
                } catch (refreshError) {
                    console.error('Error refreshing draft orders:', refreshError);
                }
            }

            alert('Draft saved successfully!');
            dispatch({ type: "CLEAR" });
            setCurrentOrderIdempotencyKey(null);
        } catch (error) {
            alert(`Error saving draft: ${error}`);
        } finally {
            if (busySet) {
                dispatch({ type: "BUSY", value: false });
            }
            actionLock.current = false;
        }
    };

    const handlePrint = async () => {
        // For dine-in: Save order → Hold → Print KOT
        // For resumed orders: Update order → Print new items
        // For takeaway/delivery: Just print KOT if order exists
        const printSettings = await fetchPrintBehaviorSettings();

        if (actionLock.current) return;
        actionLock.current = true;
        let busySet = false;

        if (state.items.length === 0) {
            alert('No items to print!');
            actionLock.current = false;
            return;
        }

        // Validate table and order taker for dine-in orders
        if (state.orderType === 'dinein') {
            if (!state.tableNo || state.tableNo === '') {
                alert('Please select a table!');
                actionLock.current = false;
                return;
            }
            if (!state.waiterID || state.waiterID === '') {
                alert('Please select an Order Taker!');
                actionLock.current = false;
                return;
            }
        }

        dispatch({ type: "BUSY", value: true });
        busySet = true;

        try {
            let orderIDToUse = state.currentOrderID;

            // If resumed order exists, update it first with new items
            if (state.currentOrderID) {
                const updateData = {
                    orderID: state.currentOrderID,
                    items: state.items.map(buildOrderItemPayload),
                    payments: state.payments.filter(p => p.amount > 0).map(p => ({
                        method: p.method,
                        amount: Number(p.amount)
                    })),
                    subTotal: totals.subTotal,
                    serviceCharge: totals.service,
                    taxAmount: totals.tax,
                    deliveryFee: totals.delivery,
                    couponCode: state.couponCode || null,
                    couponAmount: totals.coupon,
                    overallDiscount: totals.overallDiscount,
                    grandTotal: totals.grand,
                    remarks: state.notes || null
                };

                const updateResult = await dispatchRedux(updateOrder(updateData)).unwrap();
                console.log('🔄 Update Result:', updateResult);

                // Print cancellation KOT using backend-detected changes (more reliable)
                if (updateResult.itemChanges && updateResult.itemChanges.length > 0) {
                    console.log('📋 All changes detected:', updateResult.itemChanges);
                    // Filter only DECREASE and CANCEL types for cancellation KOT
                    const cancellations = updateResult.itemChanges.filter(
                        c => c.changeType === 'DECREASE' || c.changeType === 'CANCEL'
                    );
                    console.log('🚫 Cancellations to print:', cancellations);
                    if (cancellations.length > 0) {
                        console.log('🖨️ Calling printCancellationKOT...');
                        await printCancellationKOT(state.currentOrderID, cancellations);
                    } else {
                        console.log('ℹ️ No cancellations to print');
                    }
                } else {
                    console.log('ℹ️ No changes detected in update result');
                }

                // Clear frontend changed items after printing
                dispatch({ type: "SET", payload: { changedItems: [] } });
            }
            // If dine-in and no current order, save it first
            else if ((state.orderType === 'dinein' || state.orderType === 'takeaway' || state.orderType === 'dispatch') && !state.currentOrderID) {
                // For takeaway/dispatch, validate counter selection first
                if (state.orderType === 'takeaway' && !state.takeawayCounterId) {
                    alert('Please select a Takeaway Counter!');
                    if (busySet) {
                        dispatch({ type: "BUSY", value: false });
                        busySet = false;
                    }
                    actionLock.current = false;
                    return;
                }
                if (state.orderType === 'dispatch' && !state.dispatchCounterId) {
                    alert('Please select a Dispatch Counter!');
                    if (busySet) {
                        dispatch({ type: "BUSY", value: false });
                        busySet = false;
                    }
                    actionLock.current = false;
                    return;
                }

                // Save order first
                // Generate idempotency key for new order
                const newKey = generateOrderIdempotencyKey(user?.userID, state.orderType, state.tableNo);
                setCurrentOrderIdempotencyKey(newKey);
                
                const orderData = buildOrderPayload();
                orderData.orderStatus = 'parked';
                orderData.idempotencyKey = newKey;

                const orderResult = await dispatchRedux(saveOrder(orderData)).unwrap();
                orderIDToUse = orderResult.orderID;

                // Hold the order (only for dine-in)
                if (state.orderType === 'dinein') {
                    const holdData = {
                        orderID: orderIDToUse,
                        ticketNo: `HOLD-${Date.now()}`,
                        remarks: 'Held from Print KOT'
                    };

                    await dispatchRedux(holdOrder(holdData)).unwrap();
                    await dispatchRedux(getHeldOrders()).unwrap();
                }
            } else if (!orderIDToUse) {
                alert('Please save the order first or add items!');
                actionLock.current = false;
                return;
            }

            if (!printSettings.kot) {
                let disabledMessage = 'KOT printing is disabled from Print Behavior Settings.';
                if (state.orderType === 'dinein' && state.dispatchCounterId && printSettings.completeOrder) {
                    try {
                        const documentTitle = state.currentOrderID ? 'RUNNING ORDER' : 'COMPLETE ORDER';
                        const completeOrderResponse = await axios.post(`${global.SET_API_URL}kot/printCompleteOrder`, {
                            orderID: orderIDToUse,
                            documentTitle,
                            printOptions: {
                                kotPrinterMode: printSettings.kotPrinterMode || KOT_PRINTER_MODES.NETWORK
                            }
                        });
                        showPrintPreviewDocuments(completeOrderResponse.data.previewDocuments);

                        if (completeOrderResponse.data.success) {
                            disabledMessage += `\n${documentTitle} printed to dispatch counter.`;
                        } else {
                            disabledMessage += `\n${documentTitle} print failed: ${completeOrderResponse.data.message}`;
                        }
                    } catch (completeOrderError) {
                        disabledMessage += `\nComplete order print error: ${completeOrderError.message}`;
                    }
                }

                alert(disabledMessage);
                return;
            }

            // Print KOT
            const response = await axios.post(`${global.SET_API_URL}kot/printKOT`, {
                orderID: orderIDToUse,
                userID: user?.userID,
                printOptions: {
                    kot: printSettings.kot,
                    separateItemKot: printSettings.separateItemKot,
                    kotPrinterMode: printSettings.kotPrinterMode || KOT_PRINTER_MODES.NETWORK,
                    completeOrder: printSettings.completeOrder,
                    finalBill: printSettings.finalBill,
                deliveryBarcode: printSettings.deliveryBarcode
                }
            });

            if (response.data.success) {
                showPrintPreviewDocuments(response.data.previewDocuments);
                const results = response.data.printResults || [];
                const successCount = results.filter(r => r.status === 'printed').length;
                const failCount = results.filter(r => r.status === 'failed').length;
                const skipCount = results.filter(r => r.status === 'skipped').length;

                const printedCount = Number(response.data.printedCount || 0);
                const totalItems = Number(response.data.totalItems || 0);
                if (printedCount > 0 && printedCount === totalItems) {
                    dispatch({ type: "MARK_ALL_ITEMS_KOT_PRINTED" });
                }

                // Always re-sync KOT status from backend after print to avoid stale UI badges
                try {
                    const statusResp = await axios.get(`${global.SET_API_URL}kot/getOrderKotStatus/${orderIDToUse}`);
                    if (statusResp.data?.success && Number(statusResp.data?.printedItems || 0) > 0) {
                        dispatch({ type: "MARK_ALL_ITEMS_KOT_PRINTED" });
                    }
                } catch (statusError) {
                    console.warn('Unable to refresh KOT status after print:', statusError?.message || statusError);
                }

                // Item-level exact sync: get unprinted items and update badges accordingly
                try {
                    const unprintedResp = await axios.get(`${global.SET_API_URL}kot/getUnprintedItems/${orderIDToUse}`);
                    if (unprintedResp.data?.success) {
                        const pendingItems = unprintedResp.data.items || [];
                        const unprintedMap = new Map();
                        pendingItems.forEach((item) => {
                            const key = String(item.lineKey || (Number(item.isDealItem || 0) === 1 && item.dealID
                                ? createDealLineKey(item.dealID, item.itemCode)
                                : createRegularLineKey(item.itemCode)));
                            unprintedMap.set(key, Number(unprintedMap.get(key) || 0) + Number(item.pendingQuantity || item.quantity || 0));
                        });
                        dispatch({ type: "SYNC_KOT_STATUS", unprintedMap });
                    }
                } catch (syncError) {
                    console.warn('Unable to sync per-item KOT status:', syncError?.message || syncError);
                }

                // Debug trace: DB-side status vs frontend item state
                try {
                    const debugResp = await axios.get(`${global.SET_API_URL}kot/getOrderKotDebug/${orderIDToUse}`);
                    if (debugResp.data?.success) {
                        console.log('🔎 KOT DEBUG (DB):', debugResp.data);
                        console.table((debugResp.data?.allItems || []).map((row) => ({
                            orderItemID: row.orderItemID,
                            itemCode: row.itemCode,
                            itemName: row.itemName,
                            quantity: row.quantity,
                            kotPrintedQuantity: row.kotPrintedQuantity,
                            pendingQuantity: row.pendingQuantity,
                            kotPrinted: row.kotPrinted
                        })));
                    }
                } catch (debugError) {
                    console.warn('KOT debug fetch failed:', debugError?.message || debugError);
                }

                let message = `KOT Printing Results:\n`;
                message += `✅ Printed: ${successCount}\n`;
                if (failCount > 0) message += `❌ Failed: ${failCount}\n`;
                if (skipCount > 0) message += `⚠️ Skipped: ${skipCount}\n`;
                message += `\nTotal Items: ${response.data.printedCount}/${response.data.totalItems}`;

                // For DineIn - print complete order document to dispatch counter
                if (
                    state.orderType === 'dinein'
                    && state.dispatchCounterId
                    && printSettings.completeOrder
                    && Number(response.data.printedCount || 0) > 0
                ) {
                    try {
                        // If resumed order, send "RUNNING ORDER" title, else "COMPLETE ORDER"
                        const documentTitle = state.currentOrderID ? 'RUNNING ORDER' : 'COMPLETE ORDER';
                        const completeOrderResponse = await axios.post(`${global.SET_API_URL}kot/printCompleteOrder`, {
                            orderID: orderIDToUse,
                            documentTitle: documentTitle,
                            printOptions: {
                                kotPrinterMode: printSettings.kotPrinterMode || KOT_PRINTER_MODES.NETWORK
                            }
                        });
                        showPrintPreviewDocuments(completeOrderResponse.data.previewDocuments);

                        if (completeOrderResponse.data.success) {
                            message += `\n\n📋 ${documentTitle}:\n`;
                            message += `✅ Printed to dispatch counter`;
                        } else {
                            message += `\n\n⚠️ ${documentTitle} failed: ${completeOrderResponse.data.message}`;
                        }
                    } catch (completeOrderError) {
                        console.error('Complete order print error:', completeOrderError);
                        message += `\n\n⚠️ Complete order document error: ${completeOrderError.message}`;
                    }
                } else if (state.orderType === 'dinein' && state.dispatchCounterId && !printSettings.completeOrder) {
                    message += `\n\n⚠️ Complete order printing disabled from settings.`;
                }

                // Clear form after KOT and Complete Order
                if (state.orderType === 'dinein' && !state.currentOrderID) {
                    alert(message);
                    dispatch({ type: "CLEAR" });
                } else if (state.orderType === 'takeaway' || state.orderType === 'dispatch') {
                    // For takeaway, set kotPrinted flag to lock items
                    if (state.orderType === 'takeaway') {
                        dispatch({
                            type: "SET", payload: {
                                kotPrinted: true,
                                currentOrderID: orderIDToUse
                            }
                        });
                    }
                    alert(message + '\n\nOrder saved and printed successfully!');
                    // Don't clear for takeaway - need to keep order for final bill
                    if (state.orderType === 'dispatch') {
                        dispatch({ type: "CLEAR" });
                    }
                } else {
                    alert(message);
                }
            } else {
                alert('KOT printing failed: ' + response.data.message);
            }
        } catch (error) {
            console.error('KOT print error:', error);
            alert('Error: ' + (error.response?.data?.message || error.message || error));
        } finally {
            if (busySet) {
                dispatch({ type: "BUSY", value: false });
            }
            actionLock.current = false;
        }
    };

    const handlePrintTemporaryBill = async () => {
        const printSettings = await fetchPrintBehaviorSettings();

        if (actionLock.current) return;
        actionLock.current = true;
        let busySet = false;

        if (!printSettings.tempBill) {
            alert('Temporary Bill printing is disabled from Print Behavior Settings.');
            actionLock.current = false;
            return;
        }

        if (state.items.length === 0) {
            alert('No items to print!');
            actionLock.current = false;
            return;
        }

        // Non dine-in temporary bills print from cart without saving an order.
        if (effectiveOrderType !== 'dinein') {
            if (!state.takeawayCounterId) {
                alert('Please select a Takeaway Counter!');
                actionLock.current = false;
                return;
            }

            dispatch({ type: "BUSY", value: true });
            busySet = true;
            try {
                const response = await axios.post(`${global.SET_API_URL}kot/printTemporaryBillFromCart`, {
                    items: groupItemsForBill(state.items),
                    customer: {
                        name: state.customer.name || null,
                        phone: state.customer.phone || null,
                        address: state.customer.address || null
                    },
                    totals: {
                        subTotal: totals.subTotal,
                        service: totals.service,
                        tax: totals.tax,
                        delivery: totals.delivery,
                        coupon: totals.coupon,
                        overallDiscount: totals.overallDiscount,
                        grand: totals.grand
                    },
                    orderType: effectiveOrderType,
                    deliveryPaymentMethod: effectiveOrderType === 'delivery'
                        ? (isFoodpandaOrder ? 'prepaid' : state.deliveryPaymentMethod)
                        : null,
                    aggregatorPlatform: isFoodpandaOrder ? 'foodpanda' : null,
                    aggregatorOrderCode: isFoodpandaOrder ? state.foodpanda.orderId || null : null,
                    aggregatorStatus: isFoodpandaOrder ? state.foodpanda.status || null : null,
                    aggregatorRider: isFoodpandaOrder ? state.foodpanda.rider || null : null,
                    takeawayCounterId: state.takeawayCounterId,
                    userID: user?.userID
                });

                if (!response.data.success) {
                    console.warn('Temporary Bill printing failed:', response.data.message);
                }
            } catch (err) {
                console.error('Temporary bill print error:', err);
            } finally {
                if (busySet) {
                    dispatch({ type: "BUSY", value: false });
                }
                actionLock.current = false;
            }
            return;
        }

        // DineIn - requires saved order
        if (state.orderType === 'dinein') {
            if (!state.tableNo || state.tableNo === '') {
                alert('Please select a table!');
                actionLock.current = false;
                return;
            }
            if (!state.waiterID || state.waiterID === '') {
                alert('Please select an Order Taker!');
                actionLock.current = false;
                return;
            }
        }

        dispatch({ type: "BUSY", value: true });
        busySet = true;
        try {
            // Ensure order exists; if it's a new order, save it first
            let orderIDToUse = state.currentOrderID;
            if (!orderIDToUse) {
                // Generate idempotency key for new order
                const newKey = generateOrderIdempotencyKey(user?.userID, state.orderType, state.tableNo);
                setCurrentOrderIdempotencyKey(newKey);
                
                const orderData = buildOrderPayload();
                orderData.orderStatus = 'parked';
                orderData.idempotencyKey = newKey;
                
                const result = await dispatchRedux(saveOrder(orderData)).unwrap();
                orderIDToUse = result.orderID;
            }

            const resp = await axios.post(`${global.SET_API_URL}kot/printTemporaryBill`, {
                orderID: orderIDToUse,
                userID: user?.userID
            });
            if (!resp.data.success) {
                console.warn('Temporary Bill printing failed:', resp.data.message);
            }
        } catch (err) {
            console.error('Temporary bill print error:', err);
        } finally {
            if (busySet) {
                dispatch({ type: "BUSY", value: false });
            }
            actionLock.current = false;
        }
    };

    const releaseReservedTable = async (tableLabel, reasonLabel = 'flow change') => {
        if (!tableLabel) return true;

        try {
            await dispatchRedux(releaseTable({ tableLabel })).unwrap();
            await dispatchRedux(getTables()).unwrap();
            return true;
        } catch (error) {
            const errorMsg = error?.message || String(error);
            alert(`Unable to release table ${tableLabel} while ${reasonLabel}: ${errorMsg}`);
            return false;
        }
    };

    const clearDineInContext = () => {
        dispatch({
            type: "SET",
            payload: {
                tableNo: "",
                waiterID: "",
                waiter: "",
                guests: 1
            }
        });
    };

    const handleOrderTypeShortcut = async (nextOrderType, foodpandaEnabled = false) => {
        if (state.busy) return;
        if (foodpandaEnabled && !isOrderModeEnabled('foodpanda')) {
            alert('Foodpanda orders are disabled in POS Order Type Settings.');
            return;
        }
        if (!foodpandaEnabled && !isOrderModeEnabled(nextOrderType)) {
            alert('This order type is disabled in POS Order Type Settings.');
            return;
        }
        const isLeavingDineIn = state.orderType === 'dinein' && nextOrderType !== 'dinein';
        if (isLeavingDineIn && state.tableNo) {
            const released = await releaseReservedTable(state.tableNo, 'changing order type');
            if (!released) return;
        }

        const payload = {
            orderType: nextOrderType,
            dispatchCounterId: "",
            takeawayCounterId: "",
            foodpanda: {
                ...state.foodpanda,
                enabled: foodpandaEnabled
            }
        };

        if (isLeavingDineIn) {
            Object.assign(payload, {
                tableNo: "",
                waiterID: "",
                waiter: "",
                guests: 1
            });
        }

        dispatch({ type: "SET", payload });
    };

    const handleFoodpandaToggle = async (enabled) => {
        if (state.busy) return;

        const isLeavingDineIn = enabled && state.orderType === 'dinein';
        if (isLeavingDineIn && state.tableNo) {
            const released = await releaseReservedTable(state.tableNo, 'enabling Foodpanda');
            if (!released) return;
            clearDineInContext();
        }

        dispatch({
            type: "SET",
            payload: {
                foodpanda: {
                    ...state.foodpanda,
                    enabled
                }
            }
        });
    };

    const handleDirectCashShortcut = (autoCheckout = false, skipCashPromptOnAutoCheckout = false) => {
        if (state.busy) return;
        if (state.isEmployeeOrder) {
            alert('Cash payment is not applicable for Employee Orders.');
            return;
        }
        directCardCheckoutRequested.current = false;

        if (state.items.length === 0) {
            alert('No items in cart. Add items before using Direct Cash (F12).');
            directCashCheckoutRequested.current = false;
            return;
        }

        const isCODDelivery = effectiveOrderType === 'delivery' && state.deliveryPaymentMethod === 'cod';
        if (isCODDelivery || isFoodpandaOrder) {
            alert('Direct Cash (F12) is not applicable for COD/Foodpanda delivery orders.');
            directCashCheckoutRequested.current = false;
            return;
        }

        const grandTotal = Number(totals.grand || 0);
        if (grandTotal <= 0) {
            alert('Grand total must be greater than 0 for Direct Cash (F12).');
            directCashCheckoutRequested.current = false;
            return;
        }

        const amount = String(grandTotal);
        dispatch({
            type: "SET",
            payload: {
                payments: [{ method: "Cash", amount }]
            }
        });

        directCashCheckoutRequested.current = autoCheckout;
        if (autoCheckout) {
            cashPromptBypassNextCheckout.current = !!skipCashPromptOnAutoCheckout;
        }
    };

    const handleDirectCardShortcut = (autoCheckout = false) => {
        if (state.busy) return;
        if (state.isEmployeeOrder) {
            alert('Card payment is not applicable for Employee Orders.');
            return;
        }
        directCashCheckoutRequested.current = false;

        if (state.items.length === 0) {
            alert('No items in cart. Add items before using Direct Card (F11).');
            directCardCheckoutRequested.current = false;
            return;
        }

        const isCODDelivery = effectiveOrderType === 'delivery' && state.deliveryPaymentMethod === 'cod';
        if (isCODDelivery || isFoodpandaOrder) {
            alert('Direct Card (F11) is not applicable for COD/Foodpanda delivery orders.');
            directCardCheckoutRequested.current = false;
            return;
        }

        const grandTotal = Number(totals.grand || 0);
        if (grandTotal <= 0) {
            alert('Grand total must be greater than 0 for Direct Card (F11).');
            directCardCheckoutRequested.current = false;
            return;
        }

        const amount = String(grandTotal);
        dispatch({
            type: "SET",
            payload: {
                payments: [{ method: "Card", amount }]
            }
        });

        directCardCheckoutRequested.current = autoCheckout;
        cashPromptBypassNextCheckout.current = false;
    };

    const getCashPaymentIndex = () => state.payments.findIndex((payment) => payment.method === 'Cash');
    const hasCashPayment = getCashPaymentIndex() !== -1;

    const shouldPromptCashCollection = () => {
        if (state.isEmployeeOrder) return false;
        if (!hasCashPayment) return false;
        if (state.busy) return false;

        const orderType = effectiveOrderType;
        const isCODDelivery = orderType === 'delivery' && state.deliveryPaymentMethod === 'cod';
        if (isCODDelivery || isFoodpandaOrder) return false;

        return cashAmountDue > 0;
    };

    const openCashCheckoutDialog = () => {
        const cashPaymentIndex = getCashPaymentIndex();
        const currentCashAmount = Number(cashPaymentIndex >= 0 ? state.payments[cashPaymentIndex]?.amount : 0);
        const sortedCashOptions = [...CASH_COLLECTION_OPTIONS].sort((left, right) => left - right);
        const suggestedOptionAmount = sortedCashOptions.find((value) => value >= cashAmountDue) || cashAmountDue;
        const suggestedAmount = currentCashAmount > 0
            ? currentCashAmount
            : suggestedOptionAmount;

        setCashReceivedAmount(suggestedAmount);
        setShowCashCheckoutDialog(true);
    };

    const confirmCashCheckout = () => {
        const received = Number(cashReceivedAmount || 0);

        if (received <= 0) {
            alert('Please select received cash amount.');
            return;
        }

        if (received < cashAmountDue) {
            alert(`Received amount is less than cash due. Remaining due: ${currency(cashAmountDue - received)}`);
            return;
        }

        const cashPaymentIndexes = state.payments
            .map((payment, index) => (payment.method === 'Cash' ? index : -1))
            .filter((index) => index >= 0);

        let nextPayments;
        if (cashPaymentIndexes.length === 0) {
            nextPayments = [{ method: 'Cash', amount: String(cashAmountDue) }, ...state.payments];
        } else {
            const primaryCashIndex = cashPaymentIndexes[0];
            nextPayments = state.payments.map((payment, index) => {
                if (payment.method !== 'Cash') return payment;
                if (index === primaryCashIndex) {
                    return { ...payment, amount: String(cashAmountDue) };
                }
                return { ...payment, amount: '0' };
            });
        }

        dispatch({
            type: 'SET',
            payload: {
                payments: nextPayments
            }
        });

        setShowCashCheckoutDialog(false);
        cashPromptBypassNextCheckout.current = true;
        directCashCheckoutRequested.current = true;
    };

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.defaultPrevented) return;
            if (event.repeat) return;

            if (event.ctrlKey && String(event.key).toLowerCase() === 'm') {
                event.preventDefault();
                toggleBillingIdentity();
                return;
            }

            switch (event.key) {
                case 'F1':
                    event.preventDefault();
                    void handleOrderTypeShortcut('dinein');
                    break;
                case 'F2':
                    event.preventDefault();
                    void handleOrderTypeShortcut('takeaway');
                    break;
                case 'F3':
                    event.preventDefault();
                    void handleOrderTypeShortcut('delivery');
                    break;
                case 'F4':
                    event.preventDefault();
                    void handleOrderTypeShortcut('delivery', true);
                    break;
                case 'F6':
                    event.preventDefault();
                    handleSaveDraft();
                    break;
                case 'F7':
                    event.preventDefault();
                    handlePrint();
                    break;
                case 'F8':
                    event.preventDefault();
                    handlePrintTemporaryBill();
                    break;
                case 'F9':
                    event.preventDefault();
                    handleSave();
                    break;
                case 'F11':
                    event.preventDefault();
                    handleDirectCardShortcut(true);
                    break;
                case 'F12':
                    event.preventDefault();
                    handleDirectCashShortcut(true, true);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [state.busy, totals.grand, state.foodpanda.enabled, handleSave, handleSaveDraft, handlePrint, handlePrintTemporaryBill, billingLink]);

    const handleCloseReprintOrderViewModal = () => {
        setShowReprintOrderViewModal(false);
        setReprintOrderDetailLoading(false);
        setReprintOrderDetailError('');
        setReprintOrderDetail(null);
        setReprintOrderSource('completed');
    };

    const handleCloseReprintBillsModal = () => {
        setShowBillReprintModal(false);
        handleCloseReprintOrderViewModal();
    };

    const handleReprintOrderViewDialogClose = (_event, reason) => {
        if (reason === 'escapeKeyDown') {
            handleCloseReprintBillsModal();
            return;
        }

        handleCloseReprintOrderViewModal();
    };

    const loadReprintBillsForUser = async (targetUserID) => {
        const uniqueByOrderId = (rows = []) => {
            const uniqueRows = new Map();
            rows.forEach((row) => {
                const key = row?.orderID;
                if (key === undefined || key === null) return;
                if (!uniqueRows.has(key)) uniqueRows.set(key, row);
            });
            return Array.from(uniqueRows.values());
        };
        if (!targetUserID) return;

        setReprintBillsLoading(true);
        setReprintBills([]);
        setReprintActiveOrders([]);
        setReprintOpeningDate('');
        setReprintSearch('');
        setReprintPaymentMethodFilter('all');
        handleCloseReprintOrderViewModal();

        try {
            const response = await axios.get(`${global.SET_API_URL}kot/reprintBills/${targetUserID}`, {
                params: { requestedByUserID: user?.userID }
            });
            if (!response.data?.success) throw new Error(response.data?.message || 'Unable to load bills for reprint.');
            setReprintBills(uniqueByOrderId(response.data.bills || []));
            setReprintActiveOrders(uniqueByOrderId(response.data.activeOrders || []));
            setReprintOpeningDate(response.data.openingDate || '');
        } finally {
            setReprintBillsLoading(false);
        }
    };

    const handleOpenReprintBillsModal = async () => {
        if (actionLock.current) return;
        actionLock.current = true;

        try {
            if (!user || !user.userID) {
                alert('User not logged in. Bill reprint unavailable.');
                return;
            }

            const usersResponse = await axios.post(`${global.SET_API_URL}users/getAllUsers`, {
                cmpID: user.cmpID,
                branchID: user.branchID || user.defaultBranchID || 1,
                includeAll: true
            });
            const branchUsers = (Array.isArray(usersResponse.data) ? usersResponse.data : [])
                .filter((entry) => String(entry?.userType || '').trim().toLowerCase() !== 'ordertaker');
            setReprintUsers(branchUsers);
            setReprintSelectedUserID(String(user.userID));
            setShowBillReprintModal(true);
            await loadReprintBillsForUser(user.userID);
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            alert(`Bills load error: ${errorMsg}`);
        } finally {
            actionLock.current = false;
        }
    };

    const handleReprintUserChange = async (event) => {
        const targetUserID = String(event.target.value || '');
        setReprintSelectedUserID(targetUserID);
        try {
            await loadReprintBillsForUser(targetUserID);
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            alert(`Bills load error: ${errorMsg}`);
        }
    };

    const handleViewReprintOrder = async (orderID, source = 'completed') => {
        if (!orderID || reprintOrderDetailLoading) return;

        setShowReprintOrderViewModal(true);
        setReprintOrderDetailLoading(true);
        setReprintOrderDetailError('');
        setReprintOrderDetail(null);
        setReprintOrderSource(source);

        try {
            const userParam = reprintSelectedUserID ? `?userID=${encodeURIComponent(reprintSelectedUserID)}` : '';
            const response = await axios.get(`${global.SET_API_URL}kot/reprintOrderDetails/${orderID}${userParam}`);

            if (response.data?.success) {
                setReprintOrderDetail(response.data);
            } else {
                setReprintOrderDetailError(response.data?.message || 'Unable to load order details.');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            setReprintOrderDetailError(`Order details load error: ${errorMsg}`);
        } finally {
            setReprintOrderDetailLoading(false);
        }
    };

    const handleReprintFinalBill = async (orderID) => {
        if (!orderID) return;
        if (actionLock.current) return;
        actionLock.current = true;
        let busySet = false;

        try {
            if (!user || !user.userID) {
                alert('User not logged in. Bill reprint unavailable.');
                return;
            }

            dispatch({ type: "BUSY", value: true });
            busySet = true;

            const billResponse = await axios.post(`${global.SET_API_URL}kot/printBill`, {
                orderID,
                userID: user.userID,
                isExplicitReprint: true
            });

            if (billResponse.data.success) {
                alert(`Bill reprinted to ${billResponse.data.counterName || 'printer'}: ${billResponse.data.printerIP}`);
            } else {
                alert(`Bill reprint failed: ${billResponse.data.message}`);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            alert(`Bill reprint error: ${errorMsg}`);
        } finally {
            if (busySet) {
                dispatch({ type: "BUSY", value: false });
            }
            actionLock.current = false;
        }
    };

    const handleReprintCompleteOrder = async (orderID) => {
        if (!orderID) return;
        if (actionLock.current) return;
        actionLock.current = true;
        let busySet = false;

        try {
            dispatch({ type: "BUSY", value: true });
            busySet = true;

            const response = await axios.post(`${global.SET_API_URL}kot/reprintCompleteOrder`, {
                orderID
            });

            if (response.data?.success) {
                alert(`Complete order reprinted to ${response.data.counterName || 'counter'}: ${response.data.printerIP}`);
            } else {
                alert(`Complete order reprint failed: ${response.data?.message || 'Unknown error'}`);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            alert(`Complete order reprint error: ${errorMsg}`);
        } finally {
            if (busySet) {
                dispatch({ type: "BUSY", value: false });
            }
            actionLock.current = false;
        }
    };

    const handleReprintTemporaryBill = async (orderID) => {
        if (!orderID || actionLock.current) return;
        actionLock.current = true;
        let busySet = false;
        try {
            dispatch({ type: "BUSY", value: true });
            busySet = true;
            const response = await axios.post(`${global.SET_API_URL}kot/printTemporaryBill`, {
                orderID,
                userID: user?.userID
            });
            if (response.data?.success) {
                alert(`Temporary bill reprinted to ${response.data.counterName || 'printer'}: ${response.data.printerIP}`);
            } else {
                alert(`Temporary bill reprint failed: ${response.data?.message || 'Unknown error'}`);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            alert(`Temporary bill reprint error: ${errorMsg}`);
        } finally {
            if (busySet) dispatch({ type: "BUSY", value: false });
            actionLock.current = false;
        }
    };

    const printCancellationKOT = async (orderID, changedItems) => {
        console.log('🎯 printCancellationKOT called with:', { orderID, changedItems });

        if (!orderID || !changedItems || changedItems.length === 0) {
            console.log('⚠️ Cancellation KOT skipped - missing data');
            return;
        }

        try {
            console.log('📡 Sending cancellation KOT request to backend...');
            const response = await axios.post(`${global.SET_API_URL}kot/printCancellationKOT`, {
                orderID,
                cancelledItems: changedItems
            });

            console.log('📥 Cancellation KOT response:', response.data);

            if (response.data.success) {
                console.log('✅ Cancellation KOT printed successfully', response.data);
                alert('Cancellation KOT printed successfully!');
            } else {
                console.error('❌ Cancellation KOT printing failed:', response.data.message);
                alert('Cancellation KOT printing failed: ' + response.data.message);
            }
        } catch (error) {
            console.error('💥 Error printing cancellation KOT:', error);
            alert('Error printing cancellation KOT: ' + error.message);
        }
    };

    const applyResumedOrder = (order) => {
        dispatch({
            type: "SET",
            payload: {
                currentOrderID: order.orderID,
                currentHoldID: order.holdID,
                orderType: order.orderType,
                tableNo: order.tableLabel || "",
                waiterID: order.waiterID,
                riderID: order.riderID,
                dispatchCounterId: order.dispatchCounterId || null,
                takeawayCounterId: order.takeawayCounterId || null,
                customer: {
                    name: order.customerName || "",
                    phone: order.customerPhone || "",
                    address: order.customerAddress || ""
                },
                items: (order.items || []).map(item => attachReportingMeta({
                    id: item.itemCode,
                    itemCode: item.itemCode,
                    name: item.itemName,
                    qty: item.quantity,
                    price: item.unitPrice,
                    scheduledUnitDiscount: 0,
                    manualLineDiscount: Number(item.lineDiscount || 0),
                    lineDiscount: Number(item.lineDiscount || 0),
                    note: item.notes || "",
                    isDealItem: Number(item.isDealItem || 0) === 1,
                    dealID: item.dealID || null,
                    dealName: item.dealName || null,
                    lineKey: item.lineKey || (Number(item.isDealItem || 0) === 1 && item.dealID
                        ? createDealLineKey(item.dealID, item.itemCode)
                        : createRegularLineKey(item.itemCode)),
                    dealItemBaseQty: item.dealItemBaseQty || (Number(item.isDealItem || 0) === 1 ? Number(item.quantity || 1) : null),
                    dealBundleQty: item.dealBundleQty || (Number(item.isDealItem || 0) === 1 ? 1 : null),
                    kotPrinted: Number(item.kotPrintedQuantity || 0) >= Number(item.quantity || 0) || Number(item.kotPrinted) === 1,
                    kotPrintedQuantity: item.kotPrintedQuantity || 0,
                    baseUnitName: item.baseUnitName,
                    reportingUnitName: item.reportingUnitName,
                    baseUnitsPerReportingUnit: item.baseUnitsPerReportingUnit
                })),
                payments: (order.payments || []).map(p => ({
                    method: p.paymentMethod,
                    amount: p.amount
                })),
                couponCode: order.couponCode || "",
                couponAmount: order.couponAmount || 0,
                overallDiscount: order.overallDiscount || 0,
                notes: order.orderRemarks || ""
            }
        });
    };

    const applyCoupon = () => {
        // very naive: 5% off upto PKR 300 when code is FP5
        if (state.couponCode.trim().toUpperCase() === "FP5") {
            const { subTotal } = calcTotals(state);
            const amt = Math.min(300, Math.round(subTotal * 0.05));
            dispatch({ type: "APPLY_COUPON", code: "FP5", amount: amt });
        } else {
            dispatch({ type: "APPLY_COUPON", code: state.couponCode, amount: 0 });
        }
    };

    const handleCheckout = async (options = {}) => {
        // Lock before the first async operation so two checkout calls cannot enter together.
        if (actionLock.current) return;
        actionLock.current = true;

        let printSettings;
        try {
            printSettings = await fetchPrintBehaviorSettings();
        } catch (error) {
            actionLock.current = false;
            alert('Unable to load print settings: ' + (error?.message || error));
            return;
        }
        const skipCashPrompt = !!options.skipCashPrompt;

        if (!skipCashPrompt && shouldPromptCashCollection()) {
            openCashCheckoutDialog();
            actionLock.current = false;
            return;
        }
        let busySet = false;
        const orderType = effectiveOrderType;

        if (orderType === "dinein" && !state.tableNo) {
            alert("Please select a Table for Dine-In order!");
            actionLock.current = false;
            return;
        }

        // Counter validation for dispatch/takeaway
        if (orderType === "dispatch" && !state.dispatchCounterId) {
            alert("Please select a Dispatch Counter!");
            actionLock.current = false;
            return;
        }

        if (orderType === "takeaway" && !state.takeawayCounterId) {
            alert("Please select a Takeaway Counter!");
            actionLock.current = false;
            return;
        }

        // Delivery validation - customer phone is mandatory (except for Foodpanda orders)
        if (orderType === "delivery" && !isFoodpandaOrder) {
            if (!state.customer.phone || state.customer.phone.trim() === "") {
                alert("Customer phone number is required for delivery orders!");
                actionLock.current = false;
                return;
            }
            // Zone validation removed - delivery zone not required
            // if (!isFoodpandaOrder && !state.deliveryZoneId) {
            //     alert("Please select a delivery zone!");
            //     actionLock.current = false;
            //     return;
            // }
        }

        if (orderType === "delivery" && !state.takeawayCounterId) {
            alert("Please select a Takeaway Counter for Delivery/Foodpanda complete order print!");
            actionLock.current = false;
            return;
        }

        // Validation: Check if payment is complete
        // For COD delivery, skip payment validation
        const isCODDelivery = orderType === 'delivery' && state.deliveryPaymentMethod === 'cod';
        const skipPaymentValidation = isCODDelivery || isFoodpandaOrder || state.isEmployeeOrder;

        if (state.isEmployeeOrder && !state.employeeID) {
            alert('Please select an employee for Employee Order!');
            actionLock.current = false;
            return;
        }

        if (!posOrderTypeSettings.allowDecimalPayments) {
            const decimalPayment = state.payments.find((payment) => {
                const value = Number(payment.amount);
                return Number.isFinite(value) && !Number.isInteger(value);
            });
            if (decimalPayment) {
                alert('Decimal payments are disabled. Please enter whole-number payment amounts.');
                actionLock.current = false;
                return;
            }
        }

        if (!skipPaymentValidation) {
            const isCashOnlyPayment =
                state.payments.length === 1 &&
                state.payments[0]?.method === 'Cash';

            if (isCashOnlyPayment) {
                const cashAmount = Number(state.payments[0]?.amount || 0);
                if (cashAmount <= 0) {
                    alert('Cash amount missing! Press F12 to auto-fill full cash payment.');
                    actionLock.current = false;
                    return;
                }
            }

            if (totals.paid === 0) {
                alert("Please add at least one payment method!");
                actionLock.current = false;
                return;
            }

            if (totals.paid < totals.grand) {
                alert(`Payment incomplete! Due amount: ${currency(totals.due)}`);
                actionLock.current = false;
                return;
            }
        }

        dispatch({ type: "BUSY", value: true });
        busySet = true;

        try {
            let orderID;

            // Check if this is a resumed order
            if (state.currentOrderID) {
                // This is a resumed order - update existing order AND mark as completed (checkout)
                const updateData = {
                    orderID: state.currentOrderID,
                    userID: user?.userID || null,
                    items: state.items.map(buildOrderItemPayload),
                    payments: state.isEmployeeOrder ? [] : state.payments.filter(p => p.amount > 0).map(p => ({
                        method: p.method,
                        amount: Number(p.amount)
                    })),
                    isEmployeeOrder: state.isEmployeeOrder ? 1 : 0,
                    employeeID: state.isEmployeeOrder ? state.employeeID || null : null,
                    subTotal: totals.subTotal,
                    serviceCharge: totals.service,
                    taxAmount: totals.tax,
                    deliveryFee: totals.delivery,
                    couponCode: state.couponCode || null,
                    couponAmount: totals.coupon,
                    overallDiscount: totals.overallDiscount,
                    grandTotal: totals.grand,
                    remarks: state.notes || null,
                    loggedInUserID: user?.userID || 1,
                    billingUserID: user?.userID || 1,
                    taxModeEnabled,
                    taxAuthority: taxModeEnabled ? billingLink?.taxAuthority : null,
                    billingIdentityLinkID: billingLink?.linkID || null,
                    isCheckout: true // Mark this as final checkout - complete order and remove from held list
                };

                const updateResult = await dispatchRedux(updateOrder(updateData)).unwrap();
                orderID = state.currentOrderID;

                // Print cancellation KOT using backend-detected changes
                if (updateResult.itemChanges && updateResult.itemChanges.length > 0) {
                    const cancellations = updateResult.itemChanges.filter(
                        c => c.changeType === 'DECREASE' || c.changeType === 'CANCEL'
                    );
                    if (cancellations.length > 0) {
                        await printCancellationKOT(orderID, cancellations);
                    }
                }
            } else {
                // This is a new order - save it
                // Reuse the key on retries so the backend returns the original order.
                // Keep retries tied to this checkout attempt. Never reuse React
                // state that may still belong to the previously cleared order.
                const newKey = checkoutIdempotencyKeyRef.current
                    || generateOrderIdempotencyKey(user?.userID, state.orderType, state.tableNo);
                checkoutIdempotencyKeyRef.current = newKey;
                setCurrentOrderIdempotencyKey(newKey);
                
                const orderData = buildOrderPayload();
                orderData.idempotencyKey = newKey;

                const result = await dispatchRedux(saveOrder(orderData)).unwrap();
                orderID = result.orderID;
            }

            if (taxModeEnabled && billingLink?.taxProfileID && orderID) {
                try { await axios.post(global.SET_API_URL + 'taxSandbox/submit', { profileID: billingLink.taxProfileID, orderID, scenario: 'ACCEPT' }); }
                catch (taxError) { console.error('Dummy tax invoice submission failed:', taxError?.response?.data || taxError); }
            }

            // For TAKEAWAY: Print KOT (kitchen-wise) + Complete Order + Final Bill automatically
            if (orderType === 'takeaway') {
                try {
                    // Step 1: Print Kitchen KOTs (backend handles Complete Order + Final Bill)
                    if (printSettings.kot) {
                        const kotResponse = await axios.post(`${global.SET_API_URL}kot/printKOT`, {
                            orderID: orderID,
                            userID: user?.userID,
                            printOptions: {
                                kot: printSettings.kot,
                                separateItemKot: printSettings.separateItemKot,
                                kotPrinterMode: printSettings.kotPrinterMode || KOT_PRINTER_MODES.NETWORK,
                                completeOrder: printSettings.completeOrder,
                                finalBill: printSettings.finalBill,
                            deliveryBarcode: printSettings.deliveryBarcode
                            }
                        });

                        if (kotResponse.data.success) {
                            showPrintPreviewDocuments(kotResponse.data.previewDocuments);
                            console.log('✅ KOT printed successfully:', kotResponse.data.printedCount, 'items');
                            console.log('📋 Print results:', kotResponse.data.printResults);

                            // Check for Complete Order and Final Bill results
                            const completeOrderResult = kotResponse.data.printResults?.find(r => r.type === 'complete_order');
                            const finalBillResult = kotResponse.data.printResults?.find(r => r.type === 'final_bill');

                            let message = `Order completed successfully! Order ID: ${orderID}\n\n`;
                            message += `✅ Kitchen KOTs: ${kotResponse.data.printedCount} items\n`;

                            if (printSettings.completeOrder) {
                                if (completeOrderResult) {
                                    message += `[${completeOrderResult.status === 'printed' ? 'OK' : 'FAILED'}] Complete Order: ${completeOrderResult.status}${completeOrderResult.message ? ` - ${completeOrderResult.message}` : ''}\n`;
                                } else {
                                    message += `⚠️ Complete Order: no printer response\n`;
                                }
                            } else {
                                message += '⚠️ Complete Order: disabled from settings\n';
                            }

                            if (printSettings.finalBill) {
                                if (finalBillResult) {
                                    message += `[${finalBillResult.status === 'printed' ? 'OK' : 'FAILED'}] Final Bill: ${finalBillResult.status}${finalBillResult.message ? ` - ${finalBillResult.message}` : ''}\n`;
                                } else {
                                    message += `⚠️ Final Bill: no printer response\n`;
                                }
                            } else {
                                message += '⚠️ Final Bill: disabled from settings\n';
                            }

                            alert(message);
                        } else {
                            console.warn('⚠️ KOT printing issue:', kotResponse.data.message);
                            alert(`Order saved but KOT printing failed: ${kotResponse.data.message}`);
                        }
                    } else {
                        let message = `Order completed successfully! Order ID: ${orderID}\n\n`;
                        message += '⚠️ Kitchen KOTs: disabled from settings\n';
                        message += printSettings.completeOrder
                            ? '⚠️ Complete Order: skipped because KOT flow disabled\n'
                            : '⚠️ Complete Order: disabled from settings\n';
                        message += printSettings.finalBill
                            ? '⚠️ Final Bill: skipped because KOT flow disabled\n'
                            : '⚠️ Final Bill: disabled from settings\n';
                        alert(message);
                    }
                } catch (kotError) {
                    console.error('❌ KOT print error:', kotError);
                    alert(`Order saved but printing failed: ${kotError.message}`);
                }
            }
            // For DELIVERY: Print KOT + Delivery Note + Bill (if prepaid)
            else if (orderType === 'delivery') {
                try {
                    const printResults = [];

                    // Step 1: Print Kitchen KOTs
                    try {
                        if (printSettings.kot) {
                            const kotResponse = await axios.post(`${global.SET_API_URL}kot/printKOT`, {
                                orderID: orderID,
                                userID: user?.userID,
                                printOptions: {
                                    kot: printSettings.kot,
                                    separateItemKot: printSettings.separateItemKot,
                                    kotPrinterMode: printSettings.kotPrinterMode || KOT_PRINTER_MODES.NETWORK,
                                    completeOrder: printSettings.completeOrder,
                                    finalBill: printSettings.finalBill,
                                deliveryBarcode: printSettings.deliveryBarcode
                                }
                            });

                            if (kotResponse.data.success) {
                                showPrintPreviewDocuments(kotResponse.data.previewDocuments);
                                printResults.push(`✅ Kitchen KOTs: ${kotResponse.data.printedCount} items`);
                                console.log('✅ KOT printed for delivery order');

                                if (printSettings.completeOrder) {
                                    const completeOrderResult = kotResponse.data.printResults?.find(r => r.type === 'complete_order');
                                    if (completeOrderResult) {
                                        printResults.push(`? Complete Order (${completeOrderResult.status})${completeOrderResult.message ? ` - ${completeOrderResult.message}` : ''} - Takeaway Counter`);
                                    } else {
                                        printResults.push('⚠️ Complete Order: no printer response');
                                    }
                                } else {
                                    printResults.push('⚠️ Complete Order: disabled from settings');
                                }

                                // Update delivery status to 'preparing' after successful KOT print
                                try {
                                    await axios.post(`${global.SET_API_URL}delivery/updateStatus`, {
                                        orderID: orderID,
                                        newStatus: 'preparing',
                                        changedBy: 1,
                                        reason: 'KOT printed - kitchen preparing order'
                                    });
                                    console.log('✅ Delivery status updated to: preparing');
                                } catch (statusError) {
                                    console.error('⚠️ Status update error:', statusError);
                                }
                            } else {
                                printResults.push(`⚠️ Kitchen KOT: ${kotResponse.data.message}`);
                            }
                        } else {
                            printResults.push('⚠️ Kitchen KOT: disabled from settings');
                            printResults.push('⚠️ Complete Order: skipped because KOT flow disabled');
                        }
                    } catch (kotError) {
                        console.error('❌ KOT print error:', kotError);
                        printResults.push(`❌ Kitchen KOT failed: ${kotError.message}`);
                    }

                    // Step 2: Print Delivery Note only for regular delivery (skip Foodpanda)
                    if (isFoodpandaOrder) {
                        printResults.push('📋 Foodpanda - Delivery Note skipped');
                    } else if (!printSettings.deliveryNote) {
                        printResults.push('⚠️ Delivery Note: disabled from central Print Behavior Settings');
                    } else {
                        try {
                            const deliveryNoteResponse = await axios.post(`${global.SET_API_URL}kot/printDeliveryNote`, {
                                orderID: orderID,
                                userID: user?.userID,
                            printOptions: {
                                deliveryBarcode: printSettings.deliveryBarcode
                            }
                            });

                            if (deliveryNoteResponse.data.success) {
                                printResults.push('✅ Delivery Note printed for rider');
                                console.log('✅ Delivery Note printed');
                            } else {
                                printResults.push(`⚠️ Delivery Note: ${deliveryNoteResponse.data.message}`);
                            }
                        } catch (noteError) {
                            console.error('❌ Delivery Note error:', noteError);
                            printResults.push(`❌ Delivery Note failed: ${noteError.message}`);
                        }
                    }

                    // Step 3: Print Bill (only if prepaid and NOT Foodpanda)
                    if (!isFoodpandaOrder) {
                        const isPrepaid = state.deliveryPaymentMethod === 'prepaid';
                        if (isPrepaid && printSettings.finalBill) {
                            try {
                                if (!user || !user.userID) {
                                    printResults.push('❌ Bill failed: User not logged in');
                                } else {
                                    const billResponse = await axios.post(`${global.SET_API_URL}kot/printBill`, {
                                        orderID: orderID,
                                        userID: user.userID
                                    });

                                    if (billResponse.data.success) {
                                        printResults.push('✅ Final Bill (Prepaid)');
                                        console.log('✅ Bill printed for prepaid delivery');
                                    } else {
                                        printResults.push(`⚠️ Bill: ${billResponse.data.message}`);
                                    }
                                }
                            } catch (billError) {
                                console.error('❌ Bill print error:', billError);
                                const errorMsg = billError.response?.data?.message || billError.message;
                                printResults.push(`❌ Bill failed: ${errorMsg}`);
                            }
                        } else if (isPrepaid && !printSettings.finalBill) {
                            printResults.push('⚠️ Final Bill: disabled from settings');
                        } else {
                            printResults.push('📝 COD - Bill will print on delivery');
                        }
                    } else {
                        printResults.push('📋 Foodpanda - Final Bill skipped');
                    }

                    // Show summary
                    const message = `Delivery Order Completed! Order ID: ${orderID}\n\n${printResults.join('\n')}`;
                    alert(message);

                } catch (deliveryError) {
                    console.error('❌ Delivery printing error:', deliveryError);
                    alert(`Order saved but printing failed: ${deliveryError.message}`);
                }
            }
            else {
                // For DineIn/Dispatch: Print Bill on checkout (KOT already printed via Print KOT button)
                try {
                    if (!printSettings.finalBill) {
                        alert(`Order completed successfully! Order ID: ${orderID}\n\nFinal Bill printing disabled from settings.`);
                    } else if (!user || !user.userID) {
                        console.error('❌ No user found for bill printing');
                        alert(`Order completed successfully! Order ID: ${orderID}\n\nNote: Bill printing unavailable - User not logged in`);
                    } else {
                        console.log('=== BILL PRINTING DEBUG ===');
                        console.log('User object:', user);
                        console.log('User ID:', user.userID);
                        console.log('User name:', user.userName);
                        console.log('Sending bill print request...');

                        const billResponse = await axios.post(`${global.SET_API_URL}kot/printBill`, {
                            orderID: orderID,
                            userID: user.userID
                        });
                        if (billResponse.data.success) {
                            console.log('✅ Bill printed successfully to:', billResponse.data.printerIP);
                            alert(`Order completed successfully! Order ID: ${orderID}\n\nBill printed to ${billResponse.data.counterName || 'printer'}: ${billResponse.data.printerIP}`);
                        } else {
                            console.warn('⚠️ Bill printing failed:', billResponse.data.message);
                            alert(`Order completed! Order ID: ${orderID}\n\nWarning: Bill printing failed - ${billResponse.data.message}`);
                        }
                    }
                } catch (billError) {
                    console.error('❌ Bill print error:', billError);
                    const errorMsg = billError.response?.data?.message || billError.message || 'Unknown error';
                    alert(`Order completed successfully! Order ID: ${orderID}\n\nNote: Bill printing failed: ${errorMsg}`);
                }
            }

            // Safety net: release any selected table after successful checkout.
            // This avoids stale reservations when users switched away from dine-in flow.
            if (state.tableNo) {
                try {
                    await dispatchRedux(releaseTable({ tableLabel: state.tableNo })).unwrap();
                    await dispatchRedux(getTables()).unwrap();
                } catch (releaseError) {
                    console.error('Error releasing table:', releaseError);
                }
            }

            // Clear form after checkout and printing
            // If needed, we can conditionally skip clearing for resumed orders
            // but current flow clears after completion
            dispatch({ type: "CLEAR" });

            checkoutIdempotencyKeyRef.current = null;
            setCurrentOrderIdempotencyKey(null);
        } catch (error) {
            alert(`Error saving order: ${error}`);
        } finally {
            if (busySet) {
                dispatch({ type: "BUSY", value: false });
            }
            actionLock.current = false;
        }
    };

    useEffect(() => {
        if (!directCashCheckoutRequested.current) return;
        if (state.busy || actionLock.current) return;

        const cashPaymentIndex = getCashPaymentIndex();
        if (cashPaymentIndex === -1) {
            directCashCheckoutRequested.current = false;
            return;
        }

        const grandTotal = Number(totals.grand || 0);
        if (grandTotal <= 0 || Number(totals.paid || 0) < grandTotal) return;

        const shouldSkipPrompt = cashPromptBypassNextCheckout.current;
        cashPromptBypassNextCheckout.current = false;
        directCashCheckoutRequested.current = false;
        void handleCheckout({ skipCashPrompt: shouldSkipPrompt });
    }, [state.busy, state.payments, totals.grand, totals.paid, handleCheckout]);

    useEffect(() => {
        if (!directCardCheckoutRequested.current) return;
        if (state.busy || actionLock.current) return;

        const cardPaymentIndex = state.payments.findIndex((payment) => payment.method === 'Card');
        if (cardPaymentIndex === -1) {
            directCardCheckoutRequested.current = false;
            return;
        }

        const cardAmount = Number(state.payments[cardPaymentIndex]?.amount || 0);
        const grandTotal = Number(totals.grand || 0);
        if (grandTotal <= 0 || cardAmount < grandTotal) return;

        directCardCheckoutRequested.current = false;
        cashPromptBypassNextCheckout.current = false;
        void handleCheckout();
    }, [state.busy, state.payments, totals.grand, totals.paid, handleCheckout]);

    if (openingGuard.checking || !openingGuard.allowed) {
        return (
            <>
                <Navbar />
                <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Loading loadingText='Checking Opening' />
                </Box>
            </>
        );
    }
    return (
        <>
            <Navbar />
            <Box sx={{ display: "flex", flexDirection: "column" }}>

                {(isLoading || state.busy) && <LinearProgress />}

                <Box sx={{ px: 1, pt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        size='small'
                        variant='contained'
                        color='warning'
                        sx={{ mr: 1 }}
                        onClick={openDeliveryCashDialog}
                        disabled={state.busy}
                    >
                        Delivery Cash
                    </Button>
                    <Box
                        title={taxModeEnabled ? ((billingLink?.taxAuthority || 'Tax') + ' mode — ' + (activeBillingName || 'Linked ID')) : 'Normal / non-GST mode'}
                        aria-label={taxModeEnabled ? 'Tax mode on' : 'Tax mode off'}
                        sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            bgcolor: taxModeEnabled ? '#ffb300' : '#b0bec5',
                            border: '2px solid',
                            borderColor: taxModeEnabled ? '#b71c1c' : '#78909c',
                            boxShadow: taxModeEnabled ? '0 0 8px 2px rgba(255, 179, 0, 0.8)' : 'none',
                            animation: taxModeEnabled ? 'gstModeBlink 0.8s ease-in-out infinite alternate' : 'none',
                            '@keyframes gstModeBlink': {
                                from: { opacity: 0.35, transform: 'scale(0.85)' },
                                to: { opacity: 1, transform: 'scale(1.2)' }
                            }
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        px: 1,
                        pt: 0.5,
                        pb: 0.5,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        bgcolor: 'background.default'
                    }}
                >
                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50', minWidth: 230 }}>
                        <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                            Shortcuts: Ctrl+M Billing ID | F1 Dine-In | F2 Takeaway | F3 Delivery | F4 Foodpanda | F6 Save Draft | F7 Print KOT | F8 Temp Bill | F9 Hold | F12 Direct Cash + Checkout | F11 Direct Card + Checkout
                        </Typography>
                    </Paper>
                </Box>

                <Grid2
                    container
                    spacing={1}
                    sx={{
                        p: 1,
                        flex: 1,
                        overflow: "hidden",
                        pointerEvents: state.busy ? "none" : "auto",
                        opacity: state.busy ? 0.6 : 1
                    }}
                >

                    {isLoading ?
                        <>
                            <Grid2 size={5} sx={{ height: "100%" }}>
                                <Loading loadingText="Items List" />
                            </Grid2>
                        </> :
                        <>
                            <Grid2 size={5} sx={{ height: "100%" }}>
                                <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                    <CardContent sx={{ pb: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Search />
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="Search menu..."
                                                value={q}
                                                onChange={(e) => setQ(e.target.value)}
                                            />
                                        </Stack>
                                        <Box
                                            sx={{
                                                mt: 1,
                                                p: 1,
                                                borderRadius: 1.5,
                                                bgcolor: 'grey.50',
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            <Tabs
                                                value={tab}
                                                onChange={(_, v) => setTab(v)}
                                                variant="standard"
                                                sx={{
                                                    minHeight: 'auto',
                                                    '& .MuiTabs-flexContainer': {
                                                        flexWrap: 'wrap',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        rowGap: 0.75,
                                                        columnGap: 0.5
                                                    },
                                                    '& .MuiTabs-indicator': {
                                                        display: 'none'
                                                    }
                                                }}
                                            >
                                                {CATEGORIES.map((c, idx) => (
                                                    <Tab
                                                        key={c}
                                                        label={c}
                                                        onClick={() => setCategory(c)}
                                                        value={idx}
                                                        disableRipple
                                                        sx={{
                                                            minHeight: 34,
                                                            height: 34,
                                                            textTransform: 'none',
                                                            fontSize: '0.82rem',
                                                            fontWeight: tab === idx ? 700 : 500,
                                                            borderRadius: 2,
                                                            border: '1px solid',
                                                            borderColor: tab === idx ? 'primary.main' : 'divider',
                                                            bgcolor: tab === idx ? 'primary.main' : 'background.paper',
                                                            color: tab === idx ? 'primary.contrastText' : 'text.primary',
                                                            opacity: 1,
                                                            px: 0.5,
                                                            py: 0.5,
                                                            transition: 'all 0.2s ease',
                                                            '&.Mui-selected': {
                                                                color: 'primary.contrastText',
                                                                bgcolor: 'primary.main',
                                                                fontWeight: 700,
                                                                opacity: 1
                                                            },
                                                            '&:hover': {
                                                                bgcolor: tab === idx ? 'primary.dark' : 'action.hover',
                                                                borderColor: tab === idx ? 'primary.dark' : 'text.secondary'
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Tabs>
                                        </Box>
                                    </CardContent>
                                    <Divider />
                                    <Box sx={{ p: 1, overflow: "auto" }}>
                                        <Grid2 container spacing={1}>
                                            {menuCards.map((m) => (
                                                <Grid2 size={3} key={m.id} >
                                                    <Paper
                                                        onClick={() => handleMenuCardClick(m)}
                                                        sx={{
                                                            p: 1.25,
                                                            cursor: "pointer",
                                                            '&:hover': { boxShadow: 4 },
                                                            height: 108,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            justifyContent: "center",
                                                            gap: 0.75
                                                        }}
                                                    >
                                                        <Stack spacing={0.5} sx={{ width: "100%" }}>
                                                            <Typography
                                                                fontWeight={700}
                                                                fontSize={15}
                                                                lineHeight={1.2}
                                                                sx={{
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden'
                                                                }}
                                                            >
                                                                {m.name}
                                                            </Typography>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                                    <Chip
                                                                        label={m.category}
                                                                        size="small"
                                                                        sx={{ fontSize: 12 }}
                                                                        color={m.isDeal ? "success" : "default"}
                                                                    />
                                                                    {m.variantOptions && m.variantOptions.length > 1 && (
                                                                        <Chip
                                                                            label={`${m.variantOptions.length} options`}
                                                                            size="small"
                                                                            color="info"
                                                                            sx={{ fontSize: 11 }}
                                                                        />
                                                                    )}
                                                                </Stack>
                                                                <Typography fontSize={13} fontWeight={700}>{m.priceText}</Typography>
                                                            </Stack>
                                                        </Stack>
                                                    </Paper>
                                                </Grid2>
                                            ))}
                                        </Grid2>
                                    </Box>
                                </Card>
                            </Grid2>
                        </>}
                    {/* Left: Menu */}

                    {/* Middle: Cart */}
                    <Grid2 size={4} sx={{ height: "100%" }}>
                        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            <CardContent sx={{ pb: 1 }}>
                                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                    <ToggleButtonGroup
                                        size="small"
                                        color="primary"
                                        exclusive
                                        value={effectiveOrderType}
                                        onChange={(_, val) => {
                                            if (!val) return;
                                            if (isFoodpandaOrder && val !== 'delivery') return; // lock to delivery when Foodpanda
                                            if (!isOrderModeEnabled(val)) return;
                                            const keepFoodpandaEnabled = state.foodpanda.enabled && val === 'delivery';
                                            void handleOrderTypeShortcut(val, keepFoodpandaEnabled);
                                        }}
                                    >
                                        {restaurantOrderModeButtons.map((option) => <ToggleButton key={option.mode} value={option.mode} disabled={!isOrderModeEnabled(option.mode)}>{option.icon}{option.label}</ToggleButton>)}
                                    </ToggleButtonGroup>

                                    <Divider orientation="vertical" flexItem />

                                    <FormControlLabel
                                        control={<Switch checked={state.foodpanda.enabled} disabled={!isOrderModeEnabled('foodpanda')} onChange={(e) => void handleFoodpandaToggle(e.target.checked)} />}
                                        label={<Stack direction="row" spacing={1} alignItems="center"><QrCode2 /> <Typography>Foodpanda</Typography></Stack>}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={state.isEmployeeOrder}
                                                disabled={state.foodpanda.enabled}
                                                onChange={(event) => dispatch({
                                                    type: 'SET',
                                                    payload: {
                                                        isEmployeeOrder: event.target.checked,
                                                        employeeID: event.target.checked ? state.employeeID : '',
                                                        employeeName: event.target.checked ? state.employeeName : '',
                                                        payments: event.target.checked
                                                            ? []
                                                            : [{ method: 'Cash', amount: '' }]
                                                    }
                                                })}
                                            />
                                        }
                                        label="Employee Order"
                                    />
                                </Stack>
                            </CardContent>

                            {/* Context fields */}
                            <Box sx={{ px: 2, pb: 1 }}>
                                {state.isEmployeeOrder && (
                                    <Autocomplete
                                        sx={{ mb: 1 }}
                                        size="small"
                                        fullWidth
                                        options={companyEmployees}
                                        getOptionLabel={(option) => option.name || ''}
                                        value={companyEmployees.find(
                                            (employee) => String(employee.id) === String(state.employeeID)
                                        ) || null}
                                        onChange={(_, employee) => dispatch({
                                            type: 'SET',
                                            payload: {
                                                employeeID: employee?.id || '',
                                                employeeName: employee?.name || ''
                                            }
                                        })}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Select Employee *" />
                                        )}
                                    />
                                )}
                                {/* Counter Selection for Takeaway/Delivery */}
                                {(state.orderType === "takeaway" || state.orderType === "delivery") && (
                                    <Box sx={{ mb: 1 }}>
                                        <Autocomplete
                                            size="small"
                                            fullWidth
                                            options={takeawayCounters || []}
                                            getOptionLabel={(option) => option.counterName}
                                            value={takeawayCounters?.find(c => c.takeawayCounterId === parseInt(state.takeawayCounterId)) || null}
                                            onChange={(event, newValue) => dispatch({ type: "SET", payload: { takeawayCounterId: newValue ? newValue.takeawayCounterId : "" } })}
                                            renderInput={(params) => <TextField {...params} label="Takeaway Counter *" />}
                                            disabled={takeawayCounters.length === 0}
                                        />
                                    </Box>
                                )}

                                {state.orderType === "dispatch" && (
                                    <Box sx={{ mb: 1 }}>
                                        <Autocomplete
                                            size="small"
                                            fullWidth
                                            options={dispatchCounters || []}
                                            getOptionLabel={(option) => option.counterName}
                                            value={dispatchCounters?.find(c => c.dispatchCounterId === parseInt(state.dispatchCounterId)) || null}
                                            onChange={(event, newValue) => dispatch({ type: "SET", payload: { dispatchCounterId: newValue ? newValue.dispatchCounterId : "" } })}
                                            renderInput={(params) => <TextField {...params} label="Dispatch Counter *" />}
                                            disabled={dispatchCounters.length === 0}
                                        />
                                    </Box>
                                )}

                                {state.orderType === "dinein" && (
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel shrink></InputLabel>
                                            <Button variant="outlined" onClick={() => setShowTableModal(true)} sx={{ justifyContent: 'space-between' }}>
                                                <span>{state.tableNo ? `Table: ${state.tableNo}` : 'Select Table'}</span>
                                                <span style={{ opacity: 0.7 }}>{selectedTableTime ? formatElapsed(selectedTableTime) : ''}</span>
                                            </Button>
                                        </FormControl>
                                        <TextField
                                            size="small"
                                            label="Guests"
                                            type="number"
                                            value={state.guests}
                                            onChange={(e) => dispatch({ type: "SET", payload: { guests: Math.max(1, Number(e.target.value || 1)) } })}
                                            fullWidth
                                        />
                                        <TextField
                                            size="small"
                                            fullWidth
                                            select
                                            label="Order Taker"
                                            value={state.waiterID === null || state.waiterID === undefined ? "" : String(state.waiterID)}
                                            onChange={(e) => {
                                                const selectedWaiter = (employees || []).find(
                                                    (employee) => String(employee.id) === String(e.target.value)
                                                );

                                                dispatch({
                                                    type: "SET",
                                                    payload: {
                                                        waiterID: selectedWaiter ? selectedWaiter.id : "",
                                                        waiter: selectedWaiter ? selectedWaiter.name : "",
                                                    }
                                                });
                                            }}
                                        >
                                            <MenuItem value="">Select Order Taker</MenuItem>
                                            {(employees || []).map((employee, index) => (
                                                <MenuItem key={employee.id ?? index} value={String(employee.id)}>
                                                    {employee.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <Autocomplete
                                            size="small"
                                            fullWidth
                                            options={dispatchCounters || []}
                                            getOptionLabel={(option) => option.counterName}
                                            value={dispatchCounters?.find(c => c.dispatchCounterId === parseInt(state.dispatchCounterId)) || null}
                                            onChange={(event, newValue) => dispatch({ type: "SET", payload: { dispatchCounterId: newValue ? newValue.dispatchCounterId : "" } })}
                                            renderInput={(params) => <TextField {...params} label="Dispatch Counter" />}
                                            disabled={dispatchCounters.length === 0}
                                        />
                                    </Stack>
                                )}

                                {state.orderType === "delivery" && !state.foodpanda.enabled && (
                                    <Grid2 container spacing={1}>
                                        <Grid2 size={12} sm={6}>
                                            <TextField size="small" fullWidth label="Customer Name" value={state.customer.name} onChange={(e) => dispatch({ type: "SET", payload: { customer: { ...state.customer, name: e.target.value } } })} />
                                        </Grid2>
                                        <Grid2 size={12} sm={6}>
                                            <TextField size="small" fullWidth label="Phone *" value={state.customer.phone} onChange={(e) => dispatch({ type: "SET", payload: { customer: { ...state.customer, phone: e.target.value } } })} />
                                        </Grid2>
                                        <Grid2 size={12}>
                                            <TextField size="small" fullWidth label="Address" value={state.customer.address} onChange={(e) => dispatch({ type: "SET", payload: { customer: { ...state.customer, address: e.target.value } } })} />
                                        </Grid2>

                                        {/* Delivery Zone Selector - DISABLED (removed to avoid zone selection and delivery charges) */}

                                        {/* Rider selection for delivery (Autocomplete using EMPLOYEES dummy data) */}
                                        <Grid2 size={12} sm={6}>
                                            <Autocomplete
                                                size="small"
                                                fullWidth
                                                options={employees || []}
                                                getOptionLabel={(option) => option.name}
                                                value={employees?.find(e => e.name === state.rider) || null}
                                                onChange={(event, newValue) => dispatch({ type: "SET", payload: { rider: newValue ? newValue.name : "", riderID: newValue ? newValue.id : "" } })}
                                                renderInput={(params) => <TextField {...params} label="Rider (Assign)" />}
                                            />
                                        </Grid2>

                                        {/* Payment Method - COD or Prepaid */}
                                        <Grid2 size={12}>
                                            <FormControl component="fieldset">
                                                <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>Payment Method</Typography>
                                                <RadioGroup
                                                    row
                                                    value={state.deliveryPaymentMethod}
                                                    onChange={(e) => dispatch({ type: "SET", payload: { deliveryPaymentMethod: e.target.value } })}
                                                >
                                                    <FormControlLabel
                                                        value="cod"
                                                        control={<Radio size="small" />}
                                                        label="Cash on Delivery (COD)"
                                                    />
                                                    <FormControlLabel
                                                        value="prepaid"
                                                        control={<Radio size="small" />}
                                                        label="Prepaid (Pay Now)"
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid2>
                                    </Grid2>
                                )}

                                {state.foodpanda.enabled && (
                                    <Grid2 container spacing={1} sx={{ mt: 0.5 }}>
                                        <Grid2 size={12} sm={6}>
                                            <TextField size="small" fullWidth label="FP Order ID" value={state.foodpanda.orderId} onChange={(e) => dispatch({ type: "SET", payload: { foodpanda: { ...state.foodpanda, orderId: e.target.value } } })} />
                                        </Grid2>
                                        <Grid2 size={12} sm={6}>
                                            <TextField size="small" fullWidth label="Rider" value={state.foodpanda.rider} onChange={(e) => dispatch({ type: "SET", payload: { foodpanda: { ...state.foodpanda, rider: e.target.value } } })} />
                                        </Grid2>
                                        <Grid2 size={6} sm={3}>
                                            <TextField size="small" fullWidth label="Commission %" type="number" value={state.foodpanda.commissionPct} onChange={(e) => dispatch({ type: "SET", payload: { foodpanda: { ...state.foodpanda, commissionPct: Number(e.target.value || 0) } } })} />
                                        </Grid2>
                                        <Grid2 size={6} sm={3}>
                                            <TextField size="small" fullWidth label="Platform Fee" type="number" value={state.foodpanda.platformFee} onChange={(e) => dispatch({ type: "SET", payload: { foodpanda: { ...state.foodpanda, platformFee: Number(e.target.value || 0) } } })} />
                                        </Grid2>
                                        <Grid2 size={12} sm={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Status</InputLabel>
                                                <Select label="Status" value={state.foodpanda.status} onChange={(e) => dispatch({ type: "SET", payload: { foodpanda: { ...state.foodpanda, status: e.target.value } } })}>
                                                    {[
                                                        "Preparing",
                                                        "Picked",
                                                        "Delivered",
                                                        "Cancelled",
                                                    ].map((s) => (
                                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid2>
                                    </Grid2>
                                )}
                            </Box>

                            <Divider />

                            {/* Cart Table */}
                            <Box sx={{ p: 1, flex: 1, overflow: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                            <TableCell align="center">Qty</TableCell>
                                            <TableCell align="right">Line Disc</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cartDisplayItems.map((row) => (
                                            <Fragment key={row.key}>
                                                <TableRow hover>
                                                    <TableCell sx={{ maxWidth: 260, px: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        fontSize: 12,
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                    }}
                                                                >
                                                                    {row.name}
                                                                </Typography>
                                                                {row.isDealItem && (
                                                                    <Chip
                                                                        label="Deal"
                                                                        size="small"
                                                                        color="success"
                                                                        sx={{ height: 16, fontSize: 9, mt: 0.5 }}
                                                                    />
                                                                )}
                                                                {row.isDealItem && row.components.map((component) => (
                                                                    <Typography key={component.key} variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                                                                        {component.name}: {component.qtyLabel}
                                                                    </Typography>
                                                                ))}
                                                                <Chip
                                                                    label={isItemKotPrinted(row.rawItem)
                                                                        ? `KOT Printed (${row.rawItem.kotPrintedQuantity || row.rawItem.qty}/${row.rawItem.qty})`
                                                                        : 'KOT Not Printed'}
                                                                    size="small"
                                                                    color={isItemKotPrinted(row.rawItem) ? 'primary' : 'default'}
                                                                    sx={{ height: 16, fontSize: 9, mt: 0.5, ml: row.isDealItem ? 0.5 : 0 }}
                                                                />
                                                            </Box>

                                                            <TextField
                                                                size="small"
                                                                placeholder="Note"
                                                                value={row.note || ""}
                                                                onChange={(e) => dispatch(
                                                                    row.isDealItem
                                                                        ? { type: "UPDATE_DEAL_ITEMS", dealID: row.dealID, patch: { note: e.target.value } }
                                                                        : { type: "UPDATE_ITEM", lineKey: row.lineKey, patch: { note: e.target.value } }
                                                                )}
                                                                sx={{
                                                                    width: 140,
                                                                    '& .MuiInputBase-input': { fontSize: 12, padding: '6px 8px' },
                                                                }}
                                                                inputProps={{ maxLength: 60 }}
                                                            />
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell align="right" sx={{ fontSize: 10 }}>{row.isDealItem ? '-' : currency(row.price)}</TableCell>
                                                    <TableCell align="center">
                                                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                                            <IconButton size="small" onClick={() => dispatch({ type: "DEC", lineKey: row.lineKey })}><Remove fontSize="small" /></IconButton>
                                                            <Typography sx={{ minWidth: 40, textAlign: "center", fontSize: 12 }}>{row.qtyLabel}</Typography>
                                                            <IconButton size="small" onClick={() => dispatch({ type: "INC", lineKey: row.lineKey })}><Add fontSize="small" /></IconButton>
                                                        </Stack>
                                                    </TableCell>

                                                    {/* placeholders to keep header columns aligned */}
                                                    <TableCell />
                                                    <TableCell />
                                                    <TableCell />
                                                </TableRow>

                                                <TableRow>
                                                    <TableCell colSpan={6}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {!row.isDealItem && getScheduledDiscountTotal(row.rawItem) > 0 && (
                                                                    <Typography variant="caption" color="success.main" fontWeight={700}>
                                                                        Scheduled Discount: -{currency(getScheduledDiscountTotal(row.rawItem))}
                                                                    </Typography>
                                                                )}
                                                                <Typography variant="caption" color="text.secondary">Manual Discount</Typography>
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    value={row.rawItem?.manualLineDiscount || 0}
                                                                    onChange={(e) => dispatch({ type: "UPDATE_ITEM", lineKey: row.lineKey, patch: { manualLineDiscount: Number(e.target.value || 0) } })}
                                                                    sx={{ width: 120, ml: 1 }}
                                                                    disabled={Boolean(state.overallDiscount) || row.isDealItem}
                                                                    helperText={row.isDealItem ? "Deal discount is not editable here" : (state.overallDiscount ? "Disabled while Overall Discount is active" : "")}
                                                                />
                                                            </Box>

                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">Total</Typography>
                                                                <Typography sx={{ ml: 1, fontWeight: 700 }}>{currency(row.total)}</Typography>
                                                            </Box>

                                                            <Box>
                                                                <Button
                                                                    color="error"
                                                                    onClick={() => {
                                                                        if (isItemKotPrinted(row.rawItem) && row.rawItem.kotPrintedQuantity) {
                                                                            // Show warning for KOT-printed items
                                                                            dispatch({
                                                                                type: "SET",
                                                                                payload: {
                                                                                    pendingDeletion: {
                                                                                        itemId: row.rawItem.itemCode || row.rawItem.id,
                                                                                        lineKey: row.lineKey,
                                                                                        isDealItem: row.isDealItem || false,
                                                                                        dealID: row.dealID || null,
                                                                                        itemName: row.name,
                                                                                        quantity: row.qty,
                                                                                        kotPrintedQuantity: row.rawItem.kotPrintedQuantity,
                                                                                        wasteQty: row.rawItem.kotPrintedQuantity
                                                                                    }
                                                                                }
                                                                            });
                                                                        } else {
                                                                            // Not KOT printed, delete directly
                                                                            dispatch({ type: "REMOVE", lineKey: row.lineKey });
                                                                        }
                                                                    }}
                                                                    startIcon={<Delete />}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            </Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                                {state.items.length === 0 && (
                                    <Typography sx={{ p: 2, color: "text.secondary" }}>Cart is empty. Click items on the left to add.</Typography>
                                )}
                            </Box>

                            <Divider />

                            <Box sx={{ p: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Order Notes"
                                    value={state.notes}
                                    onChange={(e) => dispatch({ type: "SET", payload: { notes: e.target.value } })}
                                />
                            </Box>
                        </Card>
                    </Grid2>

                    {/* Right: Totals + Payments */}
                    <Grid2 size={3} sx={{ height: "100%" }}>
                        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            <CardContent sx={{ pb: 1 }}>
                                <Typography variant="subtitle1" fontWeight={700}>Charges</Typography>
                                <Stack spacing={1} sx={{ mt: 1 }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={state.applyService} onChange={(e) => dispatch({ type: "SET", payload: { applyService: e.target.checked } })} />}
                                        label={`Service Charge (${state.serviceRate}%)`}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={state.applyTax} disabled={Boolean(billingLink)} onChange={(e) => dispatch({ type: "SET", payload: { applyTax: e.target.checked } })} />}
                                        label={`Tax / VAT (${state.taxRate}%)`}
                                    />
                                    {state.orderType === "delivery" && (
                                        <TextField size="small" type="number" label="Delivery Fee" value={state.deliveryFee} onChange={(e) => dispatch({ type: "SET", payload: { deliveryFee: Number(e.target.value || 0) } })} />
                                    )}
                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            size="small"
                                            label="Coupon Code"
                                            value={state.couponCode}
                                            onChange={(e) => dispatch({ type: "SET", payload: { couponCode: e.target.value } })}
                                            fullWidth
                                        />
                                        <Button variant="outlined" onClick={applyCoupon} startIcon={<LocalOffer />}>Apply</Button>
                                    </Stack>
                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                        <TextField
                                            size="small"
                                            label="Overall Discount"
                                            type="number"
                                            value={state.overallDiscount}
                                            onChange={(e) => dispatch({ type: "SET", payload: { overallDiscount: Number(e.target.value || 0) } })}
                                            fullWidth
                                        />
                                        <Button variant="outlined" onClick={() => dispatch({ type: 'APPLY_OVERALL_DISCOUNT', amount: Number(state.overallDiscount || 0) })} startIcon={<LocalOffer />}>Apply</Button>
                                    </Stack>
                                </Stack>

                                <Divider sx={{ my: 1 }} />

                                <Stack spacing={0.5}>
                                    <Row label="Subtotal" value={currency(totals.grossSubTotal)} />
                                    {totals.scheduledDiscount > 0 && (
                                        <Row label="Scheduled Discount" value={"-" + currency(totals.scheduledDiscount)} />
                                    )}
                                    {totals.manualLineDiscount > 0 && (
                                        <Row label="Manual Discount" value={"-" + currency(totals.manualLineDiscount)} />
                                    )}
                                    <Row label="Service" value={currency(totals.service)} />
                                    <Row label="Tax" value={currency(totals.tax)} />
                                    {state.orderType === "delivery" && <Row label="Delivery" value={currency(totals.delivery)} />}
                                    <Row label="Coupon" value={"-" + currency(totals.coupon)} />
                                    <Row label="Overall Discount" value={"-" + currency(totals.overallDiscount)} />
                                    {state.foodpanda.enabled && (
                                        <>
                                            <Row label="FP Commission"
                                                value={"-" + currency(Math.round(((state.foodpanda.commissionPct || 0) / 100)
                                                    * totals.subTotal) + (state.foodpanda.platformFee || 0))}
                                            />
                                        </>
                                    )}
                                    <Divider />
                                    <Row label="Grand Total" value={<b>{currency(totals.grand)}</b>} />
                                </Stack>
                            </CardContent>

                            <Divider />

                            {/* Payments Section - Hide for COD delivery */}
                            {!state.isEmployeeOrder && !(state.orderType === 'delivery' && state.deliveryPaymentMethod === 'cod') && (
                                <>
                                    <Box sx={{ p: 1, flex: 1, overflow: "auto" }}>
                                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Payments</Typography>
                                        <Stack spacing={1}>
                                            {state.payments.map((p, idx) => (
                                                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                                        <InputLabel>Method</InputLabel>
                                                        <Select
                                                            label="Method"
                                                            value={p.method}
                                                            onChange={(e) => dispatch({ type: "UPDATE_PAYMENT", index: idx, patch: { method: e.target.value } })}
                                                        >
                                                            {[
                                                                "Cash",
                                                                "Card",
                                                                "Wallet",
                                                                "Bank",
                                                                "Voucher",
                                                            ].map((m) => (
                                                                <MenuItem key={m} value={m}>{m}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        inputProps={{ step: posOrderTypeSettings.allowDecimalPayments ? '0.01' : '1', min: '0' }}
                                                        label="Amount"
                                                        value={p.amount}
                                                        onWheel={(e) => e.target.blur()}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (!posOrderTypeSettings.allowDecimalPayments && !/^\d*$/.test(value)) return;
                                                            dispatch({ type: "UPDATE_PAYMENT", index: idx, patch: { amount: value } });
                                                        }}
                                                    />
                                                    <IconButton onClick={() => dispatch({ type: "REMOVE_PAYMENT", index: idx })}><Delete /></IconButton>
                                                </Stack>
                                            ))}
                                            <Button onClick={() => dispatch({ type: "ADD_PAYMENT" })} startIcon={<Payments />}>Add Payment</Button>
                                            <Divider />
                                            <Row label="Paid" value={currency(totals.paid)} />
                                            <Row label="Change" value={currency(totals.change)} />
                                            <Row label="Due" value={<b>{currency(totals.due)}</b>} />
                                        </Stack>
                                    </Box>

                                    <Divider />
                                </>
                            )}

                            {/* COD Message */}
                            {state.orderType === 'delivery' && state.deliveryPaymentMethod === 'cod' && (
                                <Box sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
                                    <Typography variant="body2" align="center" fontWeight={600}>
                                        💵 Cash on Delivery (COD)
                                    </Typography>
                                    <Typography variant="caption" align="center" display="block">
                                        Rider will collect Rs. {currency(totals.grand)} from customer
                                    </Typography>
                                </Box>
                            )}

                            <Divider />

                            <Stack direction="row" spacing={1} sx={{ p: 1 }}>
                                <Button fullWidth variant="outlined" startIcon={<PauseCircle />} onClick={handleSave} disabled={state.items.length === 0}>Hold</Button>
                                <Button fullWidth variant="outlined" startIcon={<Save />} onClick={handleSaveDraft} disabled={state.items.length === 0}>Save Draft</Button>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<Restore />}
                                    disabled={!heldOrders || heldOrders.length === 0}
                                    onClick={() => setShowHeldOrders(true)}
                                    sx={{ position: 'relative' }}
                                >
                                    Resume ({heldOrders?.length || 0})
                                    {hasPendingKotInHeldOrders && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '12.5%',
                                                right: 8,
                                                width: 27,
                                                height: 27,
                                                borderRadius: '50%',
                                                bgcolor: 'error.main',
                                                border: '1px solid',
                                                borderColor: 'common.white',
                                                pointerEvents: 'none'
                                            }}
                                        />
                                    )}
                                </Button>
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ p: 1, pt: 0 }}>
                                <Button fullWidth variant="outlined" startIcon={<Restore />} onClick={() => setShowDraftOrders(true)}>Draft ({draftOrders?.length || 0})</Button>
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ p: 1, pt: 0 }}>
                                {/* Takeaway: Show Temporary Bill and Checkout only (NO KOT button) */}
                                {state.orderType === 'takeaway' && (
                                    <>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="info"
                                            startIcon={<Print />}
                                            disabled={state.items.length === 0 || !state.takeawayCounterId}
                                            onClick={handlePrintTemporaryBill}
                                        >
                                            Temp Bill
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Save />}
                                            disabled={!canProceedCheckout}
                                            onClick={handleCheckout}
                                        >
                                            Checkout
                                        </Button>
                                    </>
                                )}

                                {/* DineIn: Show all buttons */}
                                {state.orderType === 'dinein' && (
                                    <>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            startIcon={<Print />}
                                            onClick={handlePrint}
                                            disabled={!canCheckout}
                                        >
                                            Print KOT
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="warning"
                                            startIcon={<Print />}
                                            disabled={!canCheckout}
                                            onClick={handlePrintTemporaryBill}
                                        >
                                            Temp Bill
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Save />}
                                            disabled={!canProceedCheckout}
                                            onClick={handleCheckout}
                                        >
                                            Checkout
                                        </Button>
                                    </>
                                )}

                                {/* Dispatch: Show KOT and Checkout */}
                                {state.orderType === 'dispatch' && (
                                    <>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            startIcon={<Print />}
                                            onClick={handlePrint}
                                            disabled={!canCheckout}
                                        >
                                            Print KOT
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Save />}
                                            disabled={!canProceedCheckout}
                                            onClick={handleCheckout}
                                        >
                                            Checkout
                                        </Button>
                                    </>
                                )}

                                {/* Delivery: Show Checkout only (KOT + Delivery Note print automatically) */}
                                {state.orderType === 'delivery' && (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="success"
                                        startIcon={<Save />}
                                        disabled={!canProceedCheckout}
                                        onClick={handleCheckout}
                                    >
                                        Complete Order
                                    </Button>
                                )}
                            </Stack>

                            {(state.orderType === 'dinein' || state.orderType === 'takeaway' || state.orderType === 'delivery') && (
                                <Stack direction="row" spacing={1} sx={{ p: 1, pt: 0 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="info"
                                        startIcon={<Print />}
                                        disabled={!canReprintBill}
                                        onClick={handleOpenReprintBillsModal}
                                    >
                                        Reprint Final Bill
                                    </Button>
                                </Stack>
                            )}
                        </Card>
                    </Grid2>
                </Grid2>

                <Dialog
                    open={showDeliveryCashDialog}
                    onClose={() => setShowDeliveryCashDialog(false)}
                    maxWidth='lg'
                    fullWidth
                    PaperProps={{ sx: { width: '95vw', maxWidth: 1100 } }}
                >
                    <DialogTitle>POS Delivery Cash Collection</DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2}>
                            {deliveryCashActionError && !deliveryCashConfirmOrder && (
                                <Alert severity='error' onClose={() => setDeliveryCashActionError('')}>
                                    {deliveryCashActionError}
                                </Alert>
                            )}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Paper variant='outlined' sx={{ p: 1.5, flex: 1 }}>
                                    <Typography variant='caption' color='text.secondary'>Pending</Typography>
                                    <Typography fontWeight={800}>
                                        {deliveryCashSummary.pendingOrders} orders — Rs. {Number(deliveryCashSummary.pendingAmount || 0).toLocaleString()}
                                    </Typography>
                                </Paper>
                                <Paper variant='outlined' sx={{ p: 1.5, flex: 1 }}>
                                    <Typography variant='caption' color='text.secondary'>Collected</Typography>
                                    <Typography fontWeight={800} color='success.main'>
                                        {deliveryCashSummary.collectedOrders} orders — Rs. {Number(deliveryCashSummary.collectedAmount || 0).toLocaleString()}
                                    </Typography>
                                </Paper>
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems='stretch'>
                                <Button sx={{ minWidth: 120, whiteSpace: 'nowrap' }} variant={deliveryCashStatus === 'pending' ? 'contained' : 'outlined'} onClick={() => changeDeliveryCashStatus('pending')}>Pending</Button>
                                <Button sx={{ minWidth: 155, whiteSpace: 'nowrap' }} variant={deliveryCashStatus === 'collected' ? 'contained' : 'outlined'} color='success' onClick={() => changeDeliveryCashStatus('collected')}>Cash Collected</Button>
                                <Button sx={{ minWidth: 90, whiteSpace: 'nowrap' }} variant={deliveryCashStatus === 'all' ? 'contained' : 'outlined'} onClick={() => changeDeliveryCashStatus('all')}>All</Button>
                                <TextField
                                    size='small'
                                    fullWidth
                                    label='Search Order, Customer or Phone'
                                    value={deliveryCashSearch}
                                    onChange={(event) => setDeliveryCashSearch(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') loadDeliveryCashOrders(deliveryCashStatus, deliveryCashSearch);
                                    }}
                                />
                                <Button sx={{ minWidth: 100 }} variant='outlined' onClick={() => loadDeliveryCashOrders(deliveryCashStatus, deliveryCashSearch)}>Search</Button>
                            </Stack>

                            {deliveryCashLoading ? (
                                <LinearProgress />
                            ) : deliveryCashRows.length === 0 ? (
                                <Typography color='text.secondary'>Is filter mein koi delivery cash order nahi mila.</Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {deliveryCashRows.map((order) => {
                                        const cashStatus = String(order.deliveryCashStatus || 'pending').toLowerCase();
                                        const amount = cashStatus === 'collected'
                                            ? Number(order.deliveryCashCollectedAmount || 0)
                                            : Number(order.amountToCollect || order.grandTotal || 0);
                                        return (
                                            <Paper key={order.orderID} variant='outlined' sx={{ p: 1.5 }}>
                                                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' spacing={1.5}>
                                                    <Box>
                                                        <Typography fontWeight={800}>
                                                            Order #{order.orderID} — Rs. {amount.toLocaleString()}
                                                        </Typography>
                                                        <Typography variant='body2'>
                                                            {order.customerName || 'Walk-in Customer'} {order.customerPhone ? `— ${order.customerPhone}` : ''}
                                                        </Typography>
                                                        <Typography variant='caption' color='text.secondary' display='block'>
                                                            Delivery: {String(order.deliveryStatus || 'pending').replaceAll('_', ' ')}
                                                            {order.riderName ? ` | Rider: ${order.riderName}` : ''}
                                                        </Typography>
                                                        {cashStatus === 'collected' && (
                                                            <Typography variant='caption' color='success.main' display='block'>
                                                                Collected by {order.collectedByName || 'User'}
                                                                {order.deliveryCashCollectedAt ? ` on ${new Date(order.deliveryCashCollectedAt).toLocaleString()}` : ''}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {cashStatus === 'pending' ? (
                                                            <Button
                                                                sx={{ minWidth: 190, whiteSpace: 'nowrap' }}
                                                                variant='contained'
                                                                color='success'
                                                                onClick={() => openDeliveryCashConfirmation(order)}
                                                            >
                                                                Mark Cash Collected
                                                            </Button>
                                                        ) : (
                                                            <Typography color='success.main' fontWeight={800}>CASH COLLECTED</Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowDeliveryCashDialog(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={Boolean(deliveryCashConfirmOrder)}
                    onClose={closeDeliveryCashConfirmation}
                    maxWidth='sm'
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <DialogTitle>Confirm Cash Collection</DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2}>
                            <Alert severity='warning'>
                                Confirm karne ke baad order cash collected mark ho jayega.
                            </Alert>
                            <Paper variant='outlined' sx={{ p: 2 }}>
                                <Typography fontWeight={800}>
                                    Order #{deliveryCashConfirmOrder?.orderID}
                                </Typography>
                                <Typography color='text.secondary'>
                                    {deliveryCashConfirmOrder?.customerName || 'Walk-in Customer'}
                                    {deliveryCashConfirmOrder?.phone ? ` — ${deliveryCashConfirmOrder.phone}` : ''}
                                </Typography>
                                <Typography variant='h6' color='success.main' fontWeight={800} sx={{ mt: 1 }}>
                                    Rs. {Number(deliveryCashConfirmOrder?.amountToCollect || deliveryCashConfirmOrder?.grandTotal || 0).toLocaleString()}
                                </Typography>
                            </Paper>
                            {deliveryCashActionError && (
                                <Alert severity='error'>
                                    {deliveryCashActionError}
                                </Alert>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button onClick={closeDeliveryCashConfirmation} disabled={deliveryCashCollecting}>
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            color='success'
                            onClick={markDeliveryCashCollected}
                            disabled={deliveryCashCollecting}
                            sx={{ minWidth: 190 }}
                        >
                            {deliveryCashCollecting ? 'Saving...' : 'Confirm Cash Collected'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={noticeDialog.open}
                    onClose={(_, reason) => {
                        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                        acknowledgeNotice();
                    }}
                    disableEscapeKeyDown
                    maxWidth="sm"
                    fullWidth
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            acknowledgeNotice();
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            bgcolor: `${(POS_NOTICE_META[noticeDialog.severity] || POS_NOTICE_META.info).color}.main`,
                            color: `${(POS_NOTICE_META[noticeDialog.severity] || POS_NOTICE_META.info).color}.contrastText`,
                        }}
                    >
                        {noticeDialog.title || "Action Required"}
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        <Typography sx={{ whiteSpace: "pre-line" }}>
                            {noticeDialog.message}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" autoFocus onClick={acknowledgeNotice}>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={printPreviewDialog.open}
                    onClose={closePrintPreviewDialog}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>Print Preview</DialogTitle>
                    <DialogContent dividers sx={{ bgcolor: '#f3f4f6' }}>
                        {printPreviewDialog.documents.length > 1 && (
                            <Tabs
                                value={printPreviewDialog.activeIndex}
                                onChange={handlePrintPreviewTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{ mb: 2, bgcolor: 'background.paper' }}
                            >
                                {printPreviewDialog.documents.map((doc, index) => (
                                    <Tab
                                        key={`${doc.title || 'document'}-${index}`}
                                        label={doc.title || `Document ${index + 1}`}
                                    />
                                ))}
                            </Tabs>
                        )}

                        {(() => {
                            const previewDocument = printPreviewDialog.documents[printPreviewDialog.activeIndex];

                            if (!previewDocument) {
                                return (
                                    <Typography color="text.secondary">
                                        No preview document available.
                                    </Typography>
                                );
                            }

                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        overflow: 'auto',
                                        maxHeight: '70vh',
                                        py: 2,
                                    }}
                                >
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            width: getReceiptPreviewWidth(previewDocument.paperMm),
                                            maxWidth: '100%',
                                            p: 1.5,
                                            bgcolor: '#fff',
                                            color: '#000',
                                            borderRadius: 0,
                                        }}
                                    >
                                        <Typography align="center" fontWeight={700}>
                                            {previewDocument.title || 'Print Preview'}
                                        </Typography>
                                        {previewDocument.sourceLabel && (
                                            <Typography
                                                align="center"
                                                variant="caption"
                                                display="block"
                                                sx={{ color: '#333', mb: 1 }}
                                            >
                                                {previewDocument.sourceLabel}
                                            </Typography>
                                        )}
                                        <Box
                                            component="pre"
                                            sx={{
                                                m: 0,
                                                whiteSpace: 'pre-wrap',
                                                fontFamily: "Consolas, 'Courier New', monospace",
                                                fontSize: 12,
                                                lineHeight: 1.25,
                                            }}
                                        >
                                            {previewDocument.plainText || ''}
                                        </Box>
                                    </Paper>
                                </Box>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                startIcon={<Print />}
                                onClick={() => printPreviewDocuments('selected')}
                            >
                                Print Selected
                            </Button>
                            {printPreviewDialog.documents.length > 1 && (
                                <Button
                                    variant="outlined"
                                    startIcon={<Print />}
                                    onClick={() => printPreviewDocuments('all')}
                                >
                                    Print All
                                </Button>
                            )}

                            {state.isEmployeeOrder && (
                                <Box sx={{ p: 2, bgcolor: 'warning.light' }}>
                                    <Typography align="center" fontWeight={700}>
                                        Employee Order: {state.employeeName || 'Select Employee'}
                                    </Typography>
                                    <Typography variant="caption" align="center" display="block">
                                        No Cash, Card, Wallet or Bank payment will be recorded.
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                        <Button onClick={closePrintPreviewDialog}>Close</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showVariantDialog}
                    onClose={closeVariantDialog}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Select Option</DialogTitle>
                    <DialogContent>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {variantDialogItem.baseName}
                            </Typography>

                            {(variantDialogItem.options || []).map((option) => (
                                <Button
                                    key={option.id}
                                    variant="outlined"
                                    onClick={() => handleVariantSelect(option)}
                                    sx={{ justifyContent: 'space-between' }}
                                >
                                    <span>{option.variantLabel || option.name}</span>
                                    <b>{currency(option.effectivePrice ?? option.price)}</b>
                                </Button>
                            ))}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeVariantDialog}>Cancel</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showCashCheckoutDialog}
                    onClose={() => {
                        setShowCashCheckoutDialog(false);
                        setCashReceivedAmount(0);
                    }}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Cash Checkout</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Customer kitne paise de raha hai?
                            </Typography>

                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {CASH_COLLECTION_OPTIONS.map((amount) => (
                                    <Button
                                        key={amount}
                                        variant={Number(cashReceivedAmount || 0) === amount ? 'contained' : 'outlined'}
                                        onClick={() => setCashReceivedAmount(amount)}
                                    >
                                        {currency(amount)}
                                    </Button>
                                ))}
                            </Stack>

                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Stack spacing={0.5}>
                                    <Row label="Bill Amount" value={<b>{currency(totals.grand)}</b>} />
                                    {nonCashPaidAmount > 0 && (
                                        <Row label="Other Payments" value={<b>{currency(nonCashPaidAmount)}</b>} />
                                    )}
                                    <Row label="Cash Due" value={<b>{currency(cashAmountDue)}</b>} />
                                    <Row label="Collected Amount" value={<b>{currency(cashReceivedAmount)}</b>} />
                                    <Divider sx={{ my: 0.5 }} />
                                    <Row label="Change Return" value={<b>{currency(cashChangeAmount)}</b>} />
                                </Stack>
                                {cashRemainingDue > 0 && (
                                    <Typography variant="body2" sx={{ mt: 1, color: 'error.main', fontWeight: 600 }}>
                                        Insufficient amount. Remaining due: {currency(cashRemainingDue)}
                                    </Typography>
                                )}
                            </Paper>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setShowCashCheckoutDialog(false);
                                setCashReceivedAmount(0);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={confirmCashCheckout}
                            disabled={Number(cashReceivedAmount || 0) <= 0 || cashRemainingDue > 0}
                        >
                            Proceed Checkout
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Table Selection Modal */}
                <Dialog
                    open={showTableModal}
                    onClose={() => setShowTableModal(false)}
                    fullWidth
                    maxWidth={false}
                    PaperProps={{
                        sx: {
                            width: '95vw',
                            maxWidth: '95vw'
                        }
                    }}
                >
                    <DialogTitle>Select Table</DialogTitle>
                    <DialogContent>
                        <Box sx={{ p: 1 }}>
                            {isPosBusinessLoading && (!backendTables || backendTables.length === 0) ? (
                                <Typography align="center" sx={{ py: 3 }}>
                                    Loading tables...
                                </Typography>
                            ) : isPosBusinessError && (!backendTables || backendTables.length === 0) ? (
                                <Box sx={{ py: 2, textAlign: 'center' }}>
                                    <Typography sx={{ mb: 1.5, color: 'error.main' }}>
                                        {posBusinessMessage || 'Unable to load tables.'}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            dispatchRedux(getTables());
                                            dispatchRedux(getHeldOrders());
                                        }}
                                    >
                                        Retry
                                    </Button>
                                </Box>
                            ) : (!backendTables || backendTables.length === 0) ? (
                                <Box sx={{ py: 2, textAlign: 'center' }}>
                                    <Typography sx={{ mb: 1.5 }}>
                                        No tables found. Please configure tables in Dine In Tables setup.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={() => dispatchRedux(getTables())}
                                    >
                                        Refresh
                                    </Button>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
                                        gap: 1
                                    }}
                                >
                                    {backendTables.map((t) => {
                                        // Convert assignedAt from DB to timestamp for color calculation
                                        const assignedAtTimestamp = t.assignedAt ? new Date(t.assignedAt).getTime() : null;
                                        const col = getTableColor(assignedAtTimestamp);
                                        const isAssigned = t.status === 'reserved';
                                        const isCurrentTable = state.tableNo && t.tableLabel === state.tableNo;

                                        return (
                                            <Box key={t.reservationID}>
                                                <Paper
                                                    onClick={async () => {
                                                        // Don't allow selecting already reserved tables
                                                        if (actionLock.current) return;
                                                        if (isAssigned && !isCurrentTable) {
                                                            try {
                                                                actionLock.current = true;
                                                                dispatch({ type: "BUSY", value: true });

                                                                const result = await dispatchRedux(resumeOrderByTable({
                                                                    tableLabel: t.tableLabel
                                                                })).unwrap();

                                                                applyResumedOrder(result.order);
                                                                await dispatchRedux(getHeldOrders()).unwrap();
                                                                await dispatchRedux(getTables()).unwrap();
                                                                setShowTableModal(false);
                                                                console.info('Order resumed successfully.');
                                                            } catch (error) {
                                                                const errorMsg = error?.message || String(error);
                                                                const staleReservationError = String(errorMsg).toLowerCase().includes('no active held order found for table');

                                                                if (staleReservationError) {
                                                                    try {
                                                                        await dispatchRedux(releaseTable({ tableLabel: t.tableLabel })).unwrap();
                                                                        await dispatchRedux(getTables()).unwrap();
                                                                        console.warn(`No active held order found for ${t.tableLabel}. Stale reservation released.`);
                                                                    } catch (releaseError) {
                                                                        const releaseMsg = releaseError?.message || String(releaseError);
                                                                        console.error(`Error resuming order: ${errorMsg}. Also failed to release stale reservation: ${releaseMsg}`);
                                                                    }
                                                                } else {
                                                                    console.error(`Error resuming order: ${errorMsg}`);
                                                                }
                                                            } finally {
                                                                dispatch({ type: "BUSY", value: false });
                                                                actionLock.current = false;
                                                            }
                                                            return;
                                                        }

                                                        if (isCurrentTable) {
                                                            setShowTableModal(false);
                                                            return;
                                                        }

                                                        try {
                                                            actionLock.current = true;
                                                            dispatch({ type: "BUSY", value: true });

                                                            if (state.tableNo && state.tableNo !== t.tableLabel) {
                                                                if (state.currentOrderID) {
                                                                    await dispatchRedux(transferTable({
                                                                        orderID: state.currentOrderID,
                                                                        fromTableLabel: state.tableNo,
                                                                        toTableLabel: t.tableLabel,
                                                                        userID: user?.userID || 1
                                                                    })).unwrap();
                                                                } else {
                                                                    await dispatchRedux(releaseTable({
                                                                        tableLabel: state.tableNo
                                                                    })).unwrap();
                                                                    await dispatchRedux(assignTable({
                                                                        tableLabel: t.tableLabel,
                                                                        userID: user?.userID || 1
                                                                    })).unwrap();
                                                                }
                                                            } else {
                                                                // Call backend API to assign table
                                                                await dispatchRedux(assignTable({
                                                                    tableLabel: t.tableLabel,
                                                                    userID: user?.userID || 1
                                                                })).unwrap();
                                                            }

                                                            // Update local state
                                                            dispatch({ type: 'ASSIGN_TABLE', id: t.reservationID, label: t.tableLabel });

                                                            // Refresh table status
                                                            await dispatchRedux(getTables());

                                                            setShowTableModal(false);
                                                        } catch (error) {
                                                            console.error('Table assignment error:', error);
                                                            alert(`Error assigning table: ${error}`);
                                                        } finally {
                                                            dispatch({ type: "BUSY", value: false });
                                                            actionLock.current = false;
                                                        }
                                                    }}
                                                    sx={{
                                                        p: 1,
                                                        cursor: isAssigned && !isCurrentTable ? 'not-allowed' : 'pointer',
                                                        textAlign: 'center',
                                                        backgroundColor: col.bg,
                                                        color: col.color,
                                                        opacity: isAssigned && !isCurrentTable ? 0.7 : 1,
                                                        '&:hover': { boxShadow: isAssigned && !isCurrentTable ? 0 : 4 },
                                                    }}
                                                >
                                                    <Typography fontWeight={700}>{t.tableLabel}</Typography>
                                                    <Typography fontSize={12}>{Number(t.seatingCapacity || 4)} Persons</Typography>
                                                    <Typography fontSize={12}>{isAssigned ? formatElapsed(assignedAtTimestamp) : 'Free'}</Typography>
                                                </Paper>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowTableModal(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Quantity Reduction Warning Dialog */}
                <Dialog
                    open={!!state.pendingReduction}
                    onClose={() => dispatch({ type: "CANCEL_REDUCTION" })}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                        ⚠️ Quantity Reduction Warning
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        {state.pendingReduction && (
                            <Stack spacing={2}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {state.pendingReduction.itemName}
                                </Typography>

                                <Box sx={{ bgcolor: 'error.light', p: 2, borderRadius: 1 }}>
                                    <Typography sx={{ fontWeight: 600, color: 'error.dark' }}>
                                        Kitchen ne {state.pendingReduction.kotPrintedQuantity} pieces bana diye hain!
                                    </Typography>
                                </Box>

                                <Stack spacing={1}>
                                    <Typography>
                                        <strong>Current Quantity:</strong> {state.pendingReduction.oldQty}
                                    </Typography>
                                    <Typography>
                                        <strong>New Quantity:</strong> {state.pendingReduction.newQty}
                                    </Typography>
                                    <Typography>
                                        <strong>Already Printed to Kitchen:</strong> {state.pendingReduction.kotPrintedQuantity}
                                    </Typography>
                                    <Typography sx={{ color: 'error.main', fontWeight: 600 }}>
                                        <strong>Waste:</strong> {state.pendingReduction.wasteQty} pieces
                                    </Typography>
                                </Stack>

                                <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 1 }}>
                                    <Typography>
                                        Agar aap continue karte hain to <strong>{state.pendingReduction.wasteQty} pieces waste</strong> honge.
                                        <br />
                                        Cancellation KOT print ho jayegi kitchen ko inform karne ke liye.
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => dispatch({ type: "CANCEL_REDUCTION" })}
                            variant="outlined"
                        >
                            Cancel (Keep Old Quantity)
                        </Button>
                        <Button
                            onClick={() => dispatch({ type: "CONFIRM_REDUCTION" })}
                            variant="contained"
                            color="error"
                        >
                            Yes, Continue (Print Cancellation KOT)
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Item Deletion Warning Dialog */}
                <Dialog
                    open={!!state.pendingDeletion}
                    onClose={() => dispatch({ type: "CANCEL_DELETION" })}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ bgcolor: 'error.main', color: 'error.contrastText' }}>
                        ⚠️ Item Deletion Warning
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        {state.pendingDeletion && (
                            <Stack spacing={2}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {state.pendingDeletion.itemName}
                                </Typography>

                                <Box sx={{ bgcolor: 'error.light', p: 2, borderRadius: 1 }}>
                                    <Typography sx={{ fontWeight: 600, color: 'error.dark' }}>
                                        Ye item kitchen mein {state.pendingDeletion.kotPrintedQuantity} quantity already ban chuka hai!
                                    </Typography>
                                </Box>

                                <Stack spacing={1}>
                                    <Typography>
                                        <strong>Item Quantity:</strong> {state.pendingDeletion.quantity}
                                    </Typography>
                                    <Typography>
                                        <strong>Already Printed to Kitchen:</strong> {state.pendingDeletion.kotPrintedQuantity}
                                    </Typography>
                                    <Typography sx={{ color: 'error.main', fontWeight: 600, fontSize: '1.2rem' }}>
                                        <strong>Total Waste:</strong> {state.pendingDeletion.wasteQty} pieces 🗑️
                                    </Typography>
                                </Stack>

                                <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 1 }}>
                                    <Typography>
                                        Agar aap delete karte hain to <strong>{state.pendingDeletion.wasteQty} pieces pura waste</strong> hoga.
                                        <br />
                                        <br />
                                        Cancellation KOT kitchen ko print ho jayegi is item ko stop karne ke liye.
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => dispatch({ type: "CANCEL_DELETION" })}
                            variant="outlined"
                        >
                            Cancel (Keep Item)
                        </Button>
                        <Button
                            onClick={() => dispatch({ type: "CONFIRM_DELETION" })}
                            variant="contained"
                            color="error"
                        >
                            Yes, Delete Item (Print Cancellation KOT)
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Held Orders Viewer */}
                <Dialog open={showHeldOrders} onClose={() => setShowHeldOrders(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Held Orders ({heldOrders?.length || 0})</DialogTitle>
                    <DialogContent>
                        {heldOrders && heldOrders.length > 0 ? (
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                {sortedHeldOrders.map((order) => (
                                    <Paper key={order.holdID} sx={{ p: 2 }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="h6">{order.ticketNo}</Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip label={order.orderType} size="small" color="primary" />
                                                    {/* KOT Status Chip */}
                                                    {Number(kotStatuses[order.orderID]?.totalItems || 0) === 0 ? (
                                                        <Chip label="No KOT Items" size="small" />
                                                    ) : kotStatuses[order.orderID]?.kotPrinted ? (
                                                        <Chip label={`KOT Printed (${kotStatuses[order.orderID].printedItems}/${kotStatuses[order.orderID].totalItems})`} size="small" color="success" />
                                                    ) : (
                                                        <Chip label={"Held (No KOT)"} size="small" />
                                                    )}
                                                </Stack>
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                Held: {new Date(order.heldAt).toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2">
                                                Table: {order.tableLabel || 'N/A'} | Items: {order.items?.length || 0} | Total: {currency(order.grandTotal)}
                                            </Typography>
                                            {order.customerName && (
                                                <Typography variant="body2">
                                                    Customer: {order.customerName} ({order.customerPhone})
                                                </Typography>
                                            )}
                                            {(Number(order.grandTotal || 0) <= 0 ||
                                                !String(order.tableLabel || '').trim() ||
                                                ['n/a', 'na'].includes(String(order.tableLabel || '').trim().toLowerCase())) && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={async () => {
                                                        const confirmed = window.confirm(
                                                            `Clear held order #${order.orderID}? This invalid order will be marked cancelled.`
                                                        );
                                                        if (!confirmed) return;

                                                        try {
                                                            await dispatchRedux(clearHoldOrder({
                                                                holdID: order.holdID,
                                                                orderID: order.orderID
                                                            })).unwrap();
                                                            await dispatchRedux(getHeldOrders()).unwrap();
                                                            console.info('Invalid held order cleared successfully.');
                                                        } catch (error) {
                                                            console.error('Error clearing held order:', error);
                                                            alert(error || 'Unable to clear held order.');
                                                        }
                                                    }}
                                                >
                                                    Clear Hold Order
                                                </Button>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={async () => {
                                                    try {
                                                        // Import resumeOrder at top if not already imported
                                                        // Resume the order in backend (just mark as resumed, don't create new)
                                                        await dispatchRedux(resumeOrder({
                                                            holdID: order.holdID,
                                                            orderID: order.orderID
                                                        })).unwrap();

                                                        // Restore order state in frontend
                                                        applyResumedOrder(order);

                                                        // Refresh held orders and close dialog
                                                        await dispatchRedux(getHeldOrders()).unwrap();
                                                        setShowHeldOrders(false);
                                                        console.info('Order resumed successfully.');
                                                    } catch (error) {
                                                        console.error('Error resuming order:', error);
                                                    }
                                                }}
                                            >
                                                Resume Order
                                            </Button>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        ) : (
                            <Typography>No held orders found.</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowHeldOrders(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Draft Orders Viewer */}
                <Dialog open={showDraftOrders} onClose={() => setShowDraftOrders(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Draft Orders ({draftOrders?.length || 0})</DialogTitle>
                    <DialogContent>
                        {draftOrders && draftOrders.length > 0 ? (
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                {draftOrders.map((order) => (
                                    <Paper key={order.orderID} sx={{ p: 2 }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="h6">Order #{order.orderID}</Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip label={order.orderType} size="small" color="primary" />
                                                    <Chip label="Draft (No KOT)" size="small" />
                                                </Stack>
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                Created: {order.createdAt ? new Date(order.createdAt).toLocaleString() : 'N/A'}
                                            </Typography>
                                            <Typography variant="body2">
                                                Table: {order.tableLabel || 'N/A'} | Items: {order.items?.length || 0} | Total: {currency(order.grandTotal)}
                                            </Typography>
                                            {order.customerName && (
                                                <Typography variant="body2">
                                                    Customer: {order.customerName} ({order.customerPhone})
                                                </Typography>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => {
                                                    applyResumedOrder(order);
                                                    setShowDraftOrders(false);
                                                    alert('Draft order loaded successfully!');
                                                }}
                                            >
                                                Open Draft Order
                                            </Button>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        ) : (
                            <Typography>No draft orders found.</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowDraftOrders(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showBillReprintModal}
                    onClose={handleCloseReprintBillsModal}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        <Stack spacing={1.25} sx={{ mt: 0.5 }}>
                            <Typography variant="h6">
                                Reprint Bills {reprintOpeningDate ? `(Opening Date: ${reprintOpeningDate})` : ''}
                            </Typography>
                            <FormControl fullWidth size="small">
                                <InputLabel>User</InputLabel>
                                <Select
                                    label="User"
                                    value={reprintSelectedUserID}
                                    onChange={handleReprintUserChange}
                                    disabled={reprintBillsLoading}
                                >
                                    {reprintUsers.map((entry) => (
                                        <MenuItem key={entry.userID} value={String(entry.userID)}>
                                            {entry.dispName || entry.userName} {Number(entry.userID) === Number(user?.userID) ? '(Logged In)' : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Search by KOT #, Order ID, Amount, or Payment Method"
                                    placeholder="Enter KOT #, Order ID, Amount, or Payment Method"
                                    value={reprintSearch}
                                    onChange={(e) => setReprintSearch(e.target.value)}
                                />
                                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                                    <InputLabel>Payment Method</InputLabel>
                                    <Select
                                        label="Payment Method"
                                        value={reprintPaymentMethodFilter}
                                        onChange={(e) => setReprintPaymentMethodFilter(String(e.target.value))}
                                    >
                                        <MenuItem value="all">All Methods</MenuItem>
                                        {reprintPaymentMethodOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Stack>
                    </DialogTitle>
                    <DialogContent>

                        {reprintBillsLoading ? (
                            <Typography sx={{ py: 2 }}>Loading bills...</Typography>
                        ) : (
                            <Stack spacing={1.5} sx={{ mt: 1 }}>
                                {filteredReprintBills.length > 0 && (
                                    <>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                            Completed Orders (Final Bill + Complete Order)
                                        </Typography>
                                        {filteredReprintBills.map((bill) => (
                                            <Paper key={`completed-${bill.orderID}`} sx={{ p: 1.5 }}>
                                                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle2">Order #{bill.orderID}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {bill.orderType?.toUpperCase()} | {bill.tableLabel || 'No Table'} | Rs. {Number(bill.grandTotal || 0).toLocaleString()}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            KOT #: {bill.kotNumber || bill.kotNo || 'N/A'}
                                                        </Typography>
                                                        {bill.paymentMethods && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Payment: {bill.paymentMethods}
                                                            </Typography>
                                                        )}
                                                        {String(bill.aggregatorPlatform || '').toLowerCase() === 'foodpanda' && bill.aggregatorOrderCode && (
                                                            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                                                                Foodpanda Order #: {bill.aggregatorOrderCode}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" color="text.secondary">
                                                            {bill.completedAt ? new Date(bill.completedAt).toLocaleString() : new Date(bill.createdAt).toLocaleString()}
                                                            {bill.lastBillPrintedAt ? ` | Last Printed: ${new Date(bill.lastBillPrintedAt).toLocaleString()}` : ''}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                                        <Button
                                                            variant="outlined"
                                                            color="info"
                                                            size="small"
                                                            startIcon={<Visibility />}
                                                            onClick={() => handleViewReprintOrder(bill.orderID, 'completed')}
                                                        >
                                                            View Order
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            startIcon={<Print />}
                                                            onClick={() => handleReprintFinalBill(bill.orderID)}
                                                        >
                                                            Final Bill
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="warning"
                                                            size="small"
                                                            startIcon={<Print />}
                                                            onClick={() => handleReprintCompleteOrder(bill.orderID)}
                                                        >
                                                            Complete Order
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </>
                                )}

                                {filteredReprintActiveOrders.length > 0 && (
                                    <>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'warning.main', mt: 1 }}>
                                            Active / Held Orders (Complete Order Only)
                                        </Typography>
                                        {filteredReprintActiveOrders.map((order) => (
                                            <Paper key={`active-${order.orderID}`} sx={{ p: 1.5, backgroundColor: '#fffaf2' }}>
                                                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle2">Order #{order.orderID}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {order.orderType?.toUpperCase()} | {order.tableLabel || 'No Table'} | Rs. {Number(order.grandTotal || 0).toLocaleString()}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            KOT #: {order.kotNumber || order.kotNo || 'N/A'}
                                                        </Typography>
                                                        {order.paymentMethods && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Payment: {order.paymentMethods}
                                                            </Typography>
                                                        )}
                                                        {String(order.aggregatorPlatform || '').toLowerCase() === 'foodpanda' && order.aggregatorOrderCode && (
                                                            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                                                                Foodpanda Order #: {order.aggregatorOrderCode}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(order.createdAt).toLocaleString()}
                                                            {order.orderStatus ? ` | Status: ${String(order.orderStatus).toUpperCase()}` : ''}
                                                            {order.ticketNo ? ` | Ticket: ${order.ticketNo}` : ''}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                                        <Button
                                                            variant="outlined"
                                                            color="info"
                                                            size="small"
                                                            startIcon={<Visibility />}
                                                            onClick={() => handleViewReprintOrder(order.orderID, 'active')}
                                                        >
                                                            View Order
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="info"
                                                            size="small"
                                                            startIcon={<Print />}
                                                            onClick={() => handleReprintTemporaryBill(order.orderID)}
                                                        >
                                                            Temporary Bill
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="warning"
                                                            size="small"
                                                            startIcon={<Print />}
                                                            onClick={() => handleReprintCompleteOrder(order.orderID)}
                                                        >
                                                            Complete Order
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </>
                                )}

                                {filteredReprintBills.length === 0 && filteredReprintActiveOrders.length === 0 && (
                                    <Typography sx={{ py: 2 }}>
                                        {reprintSearch.trim() || reprintPaymentMethodFilter !== 'all'
                                            ? 'No matching orders found for given KOT # / Order ID / Amount / Payment Method.'
                                            : 'No completed or active orders found for current opening date.'}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseReprintBillsModal}>Close</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showReprintOrderViewModal}
                    onClose={handleReprintOrderViewDialogClose}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>
                        <Stack spacing={0.5}>
                            <Typography variant="h6">
                                View Order {viewedReprintOrder?.orderID ? `#${viewedReprintOrder.orderID}` : ''}
                            </Typography>
                            {viewedReprintOrder && (
                                <Typography variant="body2" color="text.secondary">
                                    {String(viewedReprintOrder.orderType || '').toUpperCase() || 'N/A'}
                                    {viewedReprintOrder.kotNumber ? ` | KOT #: ${viewedReprintOrder.kotNumber}` : ''}
                                    {viewedReprintOrder.orderStatus ? ` | Status: ${String(viewedReprintOrder.orderStatus).toUpperCase()}` : ''}
                                </Typography>
                            )}
                        </Stack>
                    </DialogTitle>
                    <DialogContent dividers>
                        {reprintOrderDetailLoading ? (
                            <Typography sx={{ py: 2 }}>Loading order details...</Typography>
                        ) : reprintOrderDetailError ? (
                            <Typography color="error" sx={{ py: 2 }}>{reprintOrderDetailError}</Typography>
                        ) : viewedReprintOrder ? (
                            <Stack spacing={2}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        p: { xs: 1.5, sm: 2 },
                                        borderRadius: 1.5,
                                        border: '1px dashed #c7c7c7',
                                        backgroundColor: '#f3f3f3'
                                    }}
                                >
                                    <Paper
                                        sx={{
                                            width: 'min(100%, 380px)',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: 1,
                                            background: 'linear-gradient(180deg, #ffffff 0%, #fbfbfb 100%)',
                                            boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)',
                                            p: 1.5
                                        }}
                                    >
                                        <Typography
                                            component="pre"
                                            sx={{
                                                m: 0,
                                                fontFamily: '"Courier New", "Consolas", monospace',
                                                fontSize: { xs: '0.68rem', sm: '0.74rem' },
                                                lineHeight: 1.45,
                                                color: '#121212',
                                                whiteSpace: 'pre',
                                                overflowX: 'auto'
                                            }}
                                        >
                                            {thermalPreviewText}
                                        </Typography>
                                    </Paper>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                    Thermal-style preview generated from current order data.
                                </Typography>
                            </Stack>
                        ) : (
                            <Typography sx={{ py: 2 }}>Select an order to view details.</Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {!canPrintViewedFinalBill && viewedReprintOrder?.orderID
                                ? 'Final bill print is available for completed orders only.'
                                : 'Use buttons below to print from this order view.'}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Print />}
                                disabled={!viewedReprintOrder?.orderID || state.busy || !canPrintViewedFinalBill}
                                onClick={() => handleReprintFinalBill(viewedReprintOrder.orderID)}
                            >
                                Bill Print
                            </Button>
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<Print />}
                                disabled={!viewedReprintOrder?.orderID || state.busy}
                                onClick={() => handleReprintCompleteOrder(viewedReprintOrder.orderID)}
                            >
                                Complete Order Print
                            </Button>
                            <Button onClick={handleCloseReprintOrderViewModal}>Close</Button>
                        </Stack>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}

function Row({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">{label}</Typography>
            {typeof value === "string" || typeof value === "number" ? (
                <Typography>{value}</Typography>
            ) : (
                value
            )}
        </Stack>
    );
}
